import React, { useState, useMemo, useEffect, useRef } from 'react';
import { ChevronRight, Plus, X, Trash2, Clock, Tag, Tags, Gift, Cake, Repeat, Trophy, Users as UsersIcon, Copy, LogOut, Settings as SettingsIcon, Crown, ArrowLeft, Pencil, Send, CalendarDays, Bell, MessageSquare, Sparkles, CheckSquare } from 'lucide-react';
import BirthdayCalendar from './BirthdayCalendar';
import { expandEvents, makePresetRecurrence, detectPreset, isoWeekday } from '../utils/recurrence';
import { priorityEmoji } from '../lib/priorities';
import { useToast } from '../lib/Toast';
import { useTranslation } from '../lib/i18n';
import { useConfirm } from '../lib/ConfirmDialog';
import { useAuth } from '../lib/AuthContext';
import {
  createSharedCalendar,
  getInviteUrl,
  deleteSharedCalendar,
  leaveSharedCalendar,
  listSharedCalendarMembers,
  setMyCalendarCategory
} from '../lib/sharedCalendars';
import { AVATAR_COLORS, loadProfile } from '../lib/profile';
import { defaultData } from '../utils/storage';
import {
  format,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addDays,
  addMonths,
  subMonths,
  setMonth,
  setYear,
  isSameMonth,
  isSameDay,
  isToday,
  getWeek,
  getYear,
  parseISO
} from 'date-fns';
import { formatDate as fmt } from '../lib/dates';
import { weekdayLabels as localeWeekdays } from '../lib/i18n';
import { suggestEvents, applySuggestionTo } from '../lib/eventSuggestions';

// ---------------- Month/Year wheel picker ------------------------------------
// Two scroll-snap columns side-by-side with a centered highlight bar. Tapping
// or scrolling selects a value; the centered item becomes the "current" one.
const ITEM_H = 40; // px — matches h-10

const WheelColumn = ({ items, selectedIndex, onChange, formatItem }) => {
  const ref = React.useRef(null);
  // Scroll the initially-selected item into view on mount.
  React.useEffect(() => {
    if (ref.current) {
      ref.current.scrollTop = selectedIndex * ITEM_H;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleScroll = () => {
    if (!ref.current) return;
    clearTimeout(ref.current._snapTimer);
    ref.current._snapTimer = setTimeout(() => {
      const idx = Math.round(ref.current.scrollTop / ITEM_H);
      const clamped = Math.max(0, Math.min(items.length - 1, idx));
      if (clamped !== selectedIndex) onChange(clamped);
    }, 80);
  };

  return (
    <div
      ref={ref}
      onScroll={handleScroll}
      className="flex-1 h-[200px] overflow-y-auto snap-y snap-mandatory no-scrollbar relative z-10"
      style={{ scrollBehavior: 'smooth' }}
    >
      <div style={{ height: ITEM_H * 2 }} />
      {items.map((it, idx) => (
        <div
          key={idx}
          onClick={() => {
            onChange(idx);
            if (ref.current) ref.current.scrollTop = idx * ITEM_H;
          }}
          className={`snap-center flex items-center justify-center text-base sm:text-lg cursor-pointer transition-colors ${
            idx === selectedIndex
              ? 'text-primary-600 font-semibold'
              : 'text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300'
          }`}
          style={{ height: ITEM_H }}
        >
          {formatItem ? formatItem(it) : it}
        </div>
      ))}
      <div style={{ height: ITEM_H * 2 }} />
    </div>
  );
};

const WheelPicker = ({ months, years, selectedMonth, selectedYear, onChange }) => {
  const yearIdx = years.indexOf(selectedYear);
  return (
    <div className="absolute top-full left-0 mt-2 z-20 w-72 rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-xl overflow-hidden">
      <div className="relative flex">
        {/* Centered highlight bar */}
        <div
          className="absolute left-0 right-0 bg-gray-100 dark:bg-gray-800 pointer-events-none z-0"
          style={{ top: ITEM_H * 2, height: ITEM_H }}
          aria-hidden="true"
        />
        <WheelColumn
          items={months}
          selectedIndex={selectedMonth}
          onChange={(m) => onChange(m, selectedYear)}
        />
        <WheelColumn
          items={years}
          selectedIndex={yearIdx < 0 ? 0 : yearIdx}
          onChange={(i) => onChange(selectedMonth, years[i])}
        />
      </div>
    </div>
  );
};

// Custom dropdown that renders each option in its label color. Birthday
// labels are prefixed with a Gift icon, todo labels with a CheckSquare;
// other labels have no leading blob.
const LabelDropdown = ({ categories, value, onChange }) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const selected = categories.find((c) => c.id === value) || categories[0];
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('touchstart', onDoc);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('touchstart', onDoc);
    };
  }, [open]);
  const renderLabel = (cat) => {
    if (!cat) return null;
    return (
      <span className="flex items-center gap-2" style={{ color: cat.color }}>
        {cat.isBirthdayLabel && <Gift size={16} />}
        {cat.isTodoLabel && <CheckSquare size={16} />}
        <span className="font-semibold">{cat.name}</span>
      </span>
    );
  };
  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="input w-full text-left flex items-center justify-between"
      >
        {renderLabel(selected)}
        <ChevronRight
          size={16}
          className={`text-gray-400 transition-transform ${open ? 'rotate-90' : ''}`}
        />
      </button>
      {open && (
        <div className="absolute z-30 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-auto">
          {categories.map((cat) => (
            <button
              key={cat.id}
              type="button"
              onClick={() => {
                onChange(cat.id);
                setOpen(false);
              }}
              className={`w-full text-left px-3 py-2 hover:bg-gray-50 ${
                cat.id === value ? 'bg-gray-50' : ''
              }`}
            >
              {renderLabel(cat)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const Calendar = ({ data, updateData, reload, sharedCalendarId = null, homeSignal = 0, intent = null }) => {
  const { t, lang } = useTranslation();
  const confirm = useConfirm();
  const { user } = useAuth();
  const [currentDate, setCurrentDate] = useState(new Date());

  // Tapping the calendar's tab returns to the current month — sliding there
  // through the same carousel a swipe uses, then popping today's date so
  // the eye lands on it. `jumpTo` temporarily replaces the incoming panel
  // so the slide works even when today is many months away.
  const [jumpTo, setJumpTo] = useState(null);
  const [jumpBack, setJumpBack] = useState(false);
  const [popToday, setPopToday] = useState(false);
  const homeSignalRef = useRef(homeSignal);
  useEffect(() => {
    if (homeSignal === homeSignalRef.current) return;
    homeSignalRef.current = homeSignal;
    setSelectedDate(null);
    setDayPanelDate(null);

    const now = new Date();
    const sameMonth =
      now.getFullYear() === currentDate.getFullYear() &&
      now.getMonth() === currentDate.getMonth();
    if (sameMonth) {
      // Already here — just draw attention to today.
      setPopToday(true);
      setTimeout(() => setPopToday(false), 800);
      return;
    }

    const backwards = now < currentDate;
    const width = carouselRef.current?.offsetWidth || window.innerWidth;
    setJumpTo(now);
    setJumpBack(backwards);
    setSwipeAnim(true);
    setSwipeDx(backwards ? width : -width);
    setTimeout(() => {
      setCurrentDate(now);
      setSwipeAnim(false);
      setSwipeDx(0);
      setJumpTo(null);
      setPopToday(true);
      setTimeout(() => setPopToday(false), 800);
    }, 240);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [homeSignal]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [showAddEvent, setShowAddEvent] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [showMonthPicker, setShowMonthPicker] = useState(false);
  const [showLabelManager, setShowLabelManager] = useState(false);
  const [recurringDeleteTarget, setRecurringDeleteTarget] = useState(null); // { baseId, occurrenceDate }
  const [showCreateShared, setShowCreateShared] = useState(false);
  const [newSharedName, setNewSharedName] = useState('');
  const [newSharedWithBirthdays, setNewSharedWithBirthdays] = useState(false);
  const [creatingShared, setCreatingShared] = useState(false);
  // Shared-calendar sub-view toggle: show BirthdayCalendar inline instead
  // of the month grid when the user taps the "Birthdays" button.
  const [showSharedBirthdays, setShowSharedBirthdays] = useState(false);
  // Top-level layout switch for this view: 'calendar' (the month grid) or
  // 'activity' (a chronological feed of recent audit + comment changes
  // across every shared calendar the user can see). Toggled via the bottom
  // segmented bar.
  const [viewMode, setViewMode] = useState('calendar');
  // Natural-language quick-add input ("Zahnarzt morgen 14:00"). Parsed
  // live with chrono-node; on Enter we build a real event and skip the
  // modal entirely.
  const toast = useToast();

  // Responsive breakpoint tracking. Used so multi-day-bar absolute offsets
  // (inline styles) and the optional week-number column stay in sync.
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 639px)').matches
  );
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(max-width: 639px)');
    const handler = (e) => setIsMobile(e.matches);
    mq.addEventListener ? mq.addEventListener('change', handler) : mq.addListener(handler);
    return () => {
      mq.removeEventListener ? mq.removeEventListener('change', handler) : mq.removeListener(handler);
    };
  }, []);
  const kwColWidth = isMobile ? 0 : 50;
  // Horizontal swipe state for the month carousel. While dragging we
  // translate a 3-wide strip (prev/current/next) so the adjacent months
  // peek in under the finger.
  const swipeRef = useRef(null); // { x, y, width }
  const carouselRef = useRef(null);
  const [swipeDx, setSwipeDx] = useState(0);
  const [swipeActive, setSwipeActive] = useState(false);
  const [swipeAnim, setSwipeAnim] = useState(false);
  // The date whose events panel is currently open above the calendar.
  // Set when the user taps an already-selected day.
  const [dayPanelDate, setDayPanelDate] = useState(null);
  // Drag-to-dismiss offset for the day modal (px). Positive = dragged down.
  const [dayModalDy, setDayModalDy] = useState(0);
  const [dayModalAnim, setDayModalAnim] = useState(false);
  const dayModalDragRef = useRef(null);
  // Ref to the inner scrollable list inside the day modal. We only engage
  // the swipe-to-close gesture when this list is scrolled to the top.
  const dayModalScrollRef = useRef(null);
  const [sharedSettings, setSharedSettings] = useState(null); // { members, inviteUrl, loading, copied }
  // uid → member info, used for "created by" avatars in shared-calendar views.
  const [membersById, setMembersById] = useState({});
  // ID of the event currently shown in the detail view (tap on a list item).
  const [viewingEventId, setViewingEventId] = useState(null);
  // The concrete occurrence date the user tapped when opening a recurring
  // event's detail view. Without this the detail/edit/delete flow only knows
  // the base series row, whose `date` is the series START — so "delete only
  // this occurrence" would always hit the first occurrence.
  const [viewingOccurrenceDate, setViewingOccurrenceDate] = useState(null);
  const [commentDraft, setCommentDraft] = useState('');
  // The current user's own profile (display name + avatar). Loaded once
  // when the component mounts, so avatars on the user's own events render
  // correctly even outside shared calendars where membersById is empty.
  const [myProfile, setMyProfile] = useState(null);
  useEffect(() => {
    if (!user) return undefined;
    let cancelled = false;
    loadProfile()
      .then((p) => {
        if (!cancelled) setMyProfile(p);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [user]);

  // Resolve a userId → profile-like object. Falls back to the cached
  // current-user profile when the shared-calendar member map doesn't have
  // an entry (e.g. personal calendar, or the user opened the page before
  // the member list finished loading).
  const lookupMember = (uid) => {
    if (membersById[uid]) return membersById[uid];
    if (user && uid === user.id && myProfile) {
      return {
        userId: user.id,
        displayName: myProfile.displayName,
        avatarEmoji: myProfile.avatarEmoji,
        avatarColor: myProfile.avatarColor,
        avatarUrl: myProfile.avatarUrl,
        avatarKind: myProfile.avatarKind
      };
    }
    return null;
  };

  // Render a tiny circular avatar for an event's creator (shared view only).
  const renderCreatorAvatar = (uid, size = 22) => {
    if (!uid) return null;
    const m = lookupMember(uid);
    const color =
      AVATAR_COLORS.find((c) => c.id === m?.avatarColor) || AVATAR_COLORS[0];
    const name = m?.displayName || '?';
    const initial = name.trim().charAt(0).toUpperCase() || '?';
    if (m?.avatarKind === 'photo' && m.avatarUrl) {
      return (
        <img
          src={m.avatarUrl}
          alt=""
          title={name}
          className="rounded-full object-cover flex-shrink-0"
          style={{ width: size, height: size }}
        />
      );
    }
    return (
      <div
        title={name}
        className="rounded-full flex items-center justify-center text-white font-semibold flex-shrink-0"
        style={{ width: size, height: size, backgroundColor: color.swatch, fontSize: size * 0.45 }}
      >
        {m?.avatarEmoji ? (
          <span style={{ fontSize: size * 0.6, lineHeight: 1 }}>{m.avatarEmoji}</span>
        ) : (
          initial
        )}
      </div>
    );
  };

  // Always start on the calendar grid when the view changes.
  useEffect(() => {
    setShowSharedBirthdays(false);
  }, [sharedCalendarId]);

  // Lock body scroll while the day modal is open. Without this, iOS
  // rubber-band scrolling lets the page underneath drift around while the
  // user drags the modal. We use the `position: fixed` trick because
  // `overflow: hidden` alone is not enough to stop overscroll on iOS.
  useEffect(() => {
    if (!dayPanelDate) return undefined;
    const scrollY = window.scrollY;
    const body = document.body;
    const prev = {
      position: body.style.position,
      top: body.style.top,
      left: body.style.left,
      right: body.style.right,
      width: body.style.width,
      overflow: body.style.overflow
    };
    body.style.position = 'fixed';
    body.style.top = `-${scrollY}px`;
    body.style.left = '0';
    body.style.right = '0';
    body.style.width = '100%';
    body.style.overflow = 'hidden';
    return () => {
      body.style.position = prev.position;
      body.style.top = prev.top;
      body.style.left = prev.left;
      body.style.right = prev.right;
      body.style.width = prev.width;
      body.style.overflow = prev.overflow;
      window.scrollTo(0, scrollY);
    };
  }, [dayPanelDate]);

  useEffect(() => {
    if (!sharedCalendarId) {
      setMembersById({});
      return;
    }
    let cancelled = false;
    listSharedCalendarMembers(sharedCalendarId)
      .then((members) => {
        if (cancelled) return;
        const map = {};
        for (const m of members) map[m.userId] = m;
        setMembersById(map);
      })
      .catch(() => {
        if (!cancelled) setMembersById({});
      });
    return () => {
      cancelled = true;
    };
  }, [sharedCalendarId]);

  const sharedCalendars = data.calendar?.sharedCalendars || [];
  const currentSharedCalendar = sharedCalendarId
    ? sharedCalendars.find((c) => c.id === sharedCalendarId) || null
    : null;
  const isSharedView = !!sharedCalendarId;
  const [newEvent, setNewEvent] = useState({
    title: '',
    date: format(new Date(), 'yyyy-MM-dd'),
    endDate: '',
    startTime: '',
    endTime: '',
    allDay: false,
    categoryId: '',
    notes: '',
    recurrence: null,
    linkedSportName: null,
    participantIds: []
  });

  const allCalendarEvents = data.calendar?.events || [];
  const calendarEvents = useMemo(() => {
    if (isSharedView) {
      return allCalendarEvents.filter((ev) => ev.sharedCalendarId === sharedCalendarId);
    }
    return allCalendarEvents.filter((ev) => !ev.sharedCalendarId);
  }, [allCalendarEvents, isSharedView, sharedCalendarId]);
  const otherCalendarEvents = useMemo(() => {
    if (isSharedView) {
      return allCalendarEvents.filter((ev) => ev.sharedCalendarId !== sharedCalendarId);
    }
    return allCalendarEvents.filter((ev) => !!ev.sharedCalendarId);
  }, [allCalendarEvents, isSharedView, sharedCalendarId]);
  // Activity feed: aggregate every audit entry + chat comment from every
  // event the user can currently see, grouped by event and sorted with the
  // most-recently-active event first. Used by the bottom "Activity" tab to
  // show "what changed since I last looked" without opening every event.
  const activityFeed = useMemo(() => {
    const sharedById = new Map(
      (data.calendar?.sharedCalendars || []).map((c) => [c.id, c])
    );
    const items = [];
    for (const ev of allCalendarEvents) {
      const audit = Array.isArray(ev.audit) ? ev.audit : [];
      const comments = Array.isArray(ev.comments) ? ev.comments : [];
      if (audit.length === 0 && comments.length === 0) continue;
      const entries = [
        ...audit.map((a) => ({ ...a, kind: 'audit' })),
        ...comments.map((c) => ({ ...c, kind: 'comment' }))
      ].sort((a, b) => (b.at || '').localeCompare(a.at || ''));
      const lastAt = entries[0]?.at || '';
      items.push({
        eventId: ev.id,
        event: ev,
        sharedCalendar: ev.sharedCalendarId
          ? sharedById.get(ev.sharedCalendarId) || null
          : null,
        entries,
        lastAt
      });
    }
    items.sort((a, b) => (b.lastAt || '').localeCompare(a.lastAt || ''));
    return items.slice(0, 60);
  }, [allCalendarEvents, data.calendar?.sharedCalendars]);

  // Persist a new value for the *current* calendar's events while keeping
  // events that belong to other (personal/shared) buckets untouched.
  const commitEvents = (list) => {
    updateData({
      calendar: { ...data.calendar, events: [...otherCalendarEvents, ...list] }
    });
  };
  // Persist a new value for the FULL cross-calendar event list. Needed by
  // the detail view, which can show events from any calendar (opened via
  // the activity feed) — mapping over the scoped list there would silently
  // discard comments/edits/deletes for out-of-scope events.
  const commitAllEvents = (list) => {
    updateData({ calendar: { ...data.calendar, events: list } });
  };
  // All category rows across every calendar the user can see. Each row has
  // `sharedCalendarId` (null for personal, set for shared). The UI scopes
  // labels to the current calendar — labels edited in one calendar don't
  // affect another, but shared-calendar edits are visible to every member.
  const allCategories = data.calendar?.categories || [];

  // Rows that belong to the current view (personal vs one shared calendar).
  const scopedCategories = useMemo(
    () =>
      allCategories.filter((c) =>
        isSharedView
          ? c.sharedCalendarId === sharedCalendarId
          : !c.sharedCalendarId
      ),
    [allCategories, isSharedView, sharedCalendarId]
  );

  // For shared calendars we don't persist the 13 default color rows. Make
  // sure every default is present so the label picker always shows them,
  // overlaid with any custom renames from `scopedCategories`.
  const categories = useMemo(() => {
    if (!isSharedView) return scopedCategories;
    const defaults = defaultData.calendar.categories;
    const byId = new Map(scopedCategories.map((c) => [c.id, c]));
    return defaults.map((d) => byId.get(d.id) || { ...d, sharedCalendarId });
  }, [scopedCategories, isSharedView, sharedCalendarId]);

  // Birthday settings for the current calendar. In a shared view we use the
  // shared calendar's own columns; on the personal calendar we use the
  // global `birthdaySettings` singleton.
  const personalBirthdaySettings = data.calendar?.birthdaySettings || {
    linkedToCalendar: false,
    categoryId: 'purple'
  };
  const birthdaySettings = useMemo(() => {
    if (isSharedView && currentSharedCalendar) {
      return {
        linkedToCalendar:
          currentSharedCalendar.hasBirthdays &&
          currentSharedCalendar.birthdayLinked !== false,
        categoryId: currentSharedCalendar.birthdayCategoryId || 'purple',
        hasBirthdays: !!currentSharedCalendar.hasBirthdays
      };
    }
    return { ...personalBirthdaySettings, hasBirthdays: true };
  }, [
    isSharedView,
    currentSharedCalendar,
    personalBirthdaySettings
  ]);

  // Only consider birthdays belonging to the current calendar scope.
  const allBirthdays = data.calendar?.birthdays || [];
  const rawBirthdays = useMemo(
    () =>
      allBirthdays.filter((b) =>
        isSharedView
          ? b.sharedCalendarId === sharedCalendarId
          : !b.sharedCalendarId
      ),
    [allBirthdays, isSharedView, sharedCalendarId]
  );

  const birthdayEvents = useMemo(() => {
    if (!birthdaySettings.linkedToCalendar) return [];
    if (isSharedView && !birthdaySettings.hasBirthdays) return [];
    // Birthdays repeat every year forever. The carousel only ever renders
    // 3 months (prev / current / next), but those can straddle a year
    // boundary (December ↔ January), so we generate occurrences for every
    // year touched by [prevMonth, nextMonth] — typically 1-2 years. Each
    // occurrence carries its own age label so "(35)" updates as the user
    // scrolls forward into future years.
    const minYear = subMonths(currentDate, 1).getFullYear();
    const maxYear = addMonths(currentDate, 1).getFullYear();
    const out = [];
    for (const p of rawBirthdays) {
      // Normalize legacy format.
      let day = p.day, month = p.month, year = p.year;
      if (!day && p.birthDate) {
        const parts = p.birthDate.split('-').map(Number);
        year = parts[0]; month = parts[1]; day = parts[2];
      }
      if (!day || !month) continue;
      for (let yr = minYear; yr <= maxYear; yr++) {
        // Build via Date so Feb 29 rolls to Mar 1 in non-leap years instead
        // of producing an invalid string that matches no grid cell (the
        // birthday list rolls the same way, keeping both views consistent).
        const dateStr = format(new Date(yr, month - 1, day), 'yyyy-MM-dd');
        const age = year ? yr - year : null;
        const ageLabel = age != null && age > 0 ? ` (${age})` : '';
        out.push({
          id: `birthday-${p.id}-${yr}`,
          title: `${p.name}${ageLabel}`,
          date: dateStr,
          endDate: dateStr,
          allDay: false, // use light-bg style
          startTime: '',
          endTime: '',
          categoryId: birthdaySettings.categoryId,
          isBirthday: true,
          notes: ''
        });
      }
    }
    return out;
  }, [
    rawBirthdays,
    birthdaySettings.linkedToCalendar,
    birthdaySettings.categoryId,
    birthdaySettings.hasBirthdays,
    isSharedView,
    currentDate
  ]);

  // To-dos linked into the calendar the user chose (personal or one shared
  // calendar). Same pattern as birthdays: the events are derived from the
  // task rows rather than stored, so ticking a task off on the Todo page
  // immediately crosses it off here too. In a shared view we additionally
  // render other members' tasks shared into this calendar (read-only rows
  // the loader put into data.todo.sharedTasks).
  const todoCalendarSettings = data.todo?.calendarSettings || {
    linkedToCalendar: false,
    categoryId: 'blue',
    sharedCalendarId: null
  };
  const todoTasks = data.todo?.tasks || [];
  const sharedTodoTasks = data.todo?.sharedTasks || [];
  const todoEvents = useMemo(() => {
    let source;
    if (isSharedView) {
      // Task rows are stamped with their target calendar; the stamp implies
      // the owner's link toggle is on (turning it off restamps to null).
      source = [
        ...todoTasks.filter((task) => task.sharedCalendarId === sharedCalendarId),
        ...sharedTodoTasks.filter((task) => task.sharedCalendarId === sharedCalendarId)
      ];
    } else {
      if (
        !todoCalendarSettings.linkedToCalendar ||
        todoCalendarSettings.sharedCalendarId
      )
        return [];
      source = todoTasks.filter((task) => !task.sharedCalendarId);
    }
    return source
      .filter((task) => task.date)
      .map((task) => ({
        id: `todo-${task.id}`,
        title: task.title,
        date: task.date,
        endDate: task.date,
        allDay: false, // use light-bg style
        startTime: task.time || '',
        endTime: '',
        // Rows carry the owner's chosen color so members see it too;
        // legacy rows fall back to the viewer's own setting.
        categoryId: task.calendarCategoryId || todoCalendarSettings.categoryId,
        isTodo: true,
        done: !!task.done,
        priority: task.priority || null,
        notes: task.note || ''
      }));
  }, [
    todoTasks,
    sharedTodoTasks,
    todoCalendarSettings.linkedToCalendar,
    todoCalendarSettings.categoryId,
    todoCalendarSettings.sharedCalendarId,
    isSharedView,
    sharedCalendarId
  ]);

  // Which label colour each member's to-dos occupy in THIS calendar. Used to
  // show the colour as taken ("Janas To-dos … aus To-dos (Jana)"), the same
  // way the birthday label is reserved.
  const todoLabelOwners = useMemo(() => {
    const out = new Map(); // categoryId -> userId
    const mine = todoCalendarSettings;
    const own = data.todo?.tasks || [];
    const foreign = data.todo?.sharedTasks || [];
    if (isSharedView) {
      // My own claim first, so my name wins for a colour we both use.
      if (
        mine.linkedToCalendar &&
        mine.sharedCalendarId === sharedCalendarId &&
        mine.categoryId
      ) {
        out.set(mine.categoryId, user?.id || null);
      }
      for (const tk of [...own, ...foreign]) {
        if (tk.sharedCalendarId !== sharedCalendarId) continue;
        const cid = tk.calendarCategoryId;
        if (!cid || out.has(cid)) continue;
        out.set(cid, tk.createdBy || user?.id || null);
      }
    } else if (mine.linkedToCalendar && !mine.sharedCalendarId && mine.categoryId) {
      out.set(mine.categoryId, user?.id || null);
    }
    return out;
  }, [
    data.todo?.tasks,
    data.todo?.sharedTasks,
    todoCalendarSettings,
    isSharedView,
    sharedCalendarId,
    user
  ]);

  // events is computed after calendarDays (see below).

  // Apply birthday-label override: if a category is linked as the birthday label,
  // display it as "Birthday" with filled color (treat as a used/custom label).
  // Same for the todo label; if both link the same color, birthday wins the
  // name (todo events still render in the shared color).
  const displayCategories = useMemo(() => {
    let out = categories;
    if (birthdaySettings.linkedToCalendar) {
      out = out.map((c) =>
        c.id === birthdaySettings.categoryId
          ? { ...c, name: 'Birthday', isCustom: true, isBirthdayLabel: true }
          : c
      );
    }
    if (todoLabelOwners.size > 0) {
      out = out.map((c) => {
        if (!todoLabelOwners.has(c.id) || c.isBirthdayLabel) return c;
        const uid = todoLabelOwners.get(c.id);
        const m = uid && membersById[uid] ? membersById[uid] : null;
        const name =
          (m && m.displayName) ||
          (uid && user && uid === user.id && myProfile?.displayName) ||
          '';
        return {
          ...c,
          name: name ? t('calendar.todosOf', '{name}s To-dos', { name }) : t('todo.title'),
          isCustom: true,
          isTodoLabel: true,
          todoOwnerName: name
        };
      });
    }
    return out;
  }, [
    categories,
    birthdaySettings.linkedToCalendar,
    birthdaySettings.categoryId,
    todoLabelOwners,
    membersById,
    myProfile,
    user,
    t
  ]);

  // Custom labels float to the top, defaults stay in rainbow order
  const sortedCategories = useMemo(() => {
    const custom = displayCategories.filter((c) => c.isCustom);
    const defaults = displayCategories.filter((c) => !c.isCustom);
    return [...custom, ...defaults];
  }, [displayCategories]);

  // Categories shown in the event-form dropdown. The Birthday- and
  // Todo-linked labels are reserved for entries created from their pages,
  // so we hide them from manual event creation.
  const selectableCategories = useMemo(
    () => sortedCategories.filter((c) => !c.isBirthdayLabel && !c.isTodoLabel),
    [sortedCategories]
  );

  // Default label for new events. Resolution order:
  //   1. In a shared calendar, the current user's per-calendar preference
  //      (set via the members panel) wins, so each member can have their
  //      own colour.
  //   2. Otherwise, the label of the *current user's* most recently saved
  //      event in this calendar (NOT global, so other members' labels
  //      don't bleed into yours).
  //   3. Fall back to the first selectable label.
  const myMemberCategoryId = useMemo(() => {
    if (!isSharedView || !user) return null;
    const me = membersById[user.id];
    if (!me || !me.categoryId) return null;
    if (!selectableCategories.some((c) => c.id === me.categoryId)) return null;
    return me.categoryId;
  }, [isSharedView, user, membersById, selectableCategories]);

  const lastUsedCategoryId = useMemo(() => {
    if (myMemberCategoryId) return myMemberCategoryId;
    const myId = user?.id || null;
    const sorted = [...calendarEvents].sort((a, b) =>
      String(b.id).localeCompare(String(a.id))
    );
    const recent = sorted.find(
      (ev) =>
        ev.categoryId &&
        // Only consider this user's own events. In a personal calendar
        // every event is theirs (createdBy may be null on legacy rows,
        // which we still accept). In a shared calendar we strictly require
        // a matching createdBy so other members' choices don't show up as
        // *your* default.
        (isSharedView ? ev.createdBy === myId : !ev.createdBy || ev.createdBy === myId) &&
        selectableCategories.some((c) => c.id === ev.categoryId)
    );
    return recent?.categoryId || selectableCategories[0]?.id || '';
  }, [calendarEvents, selectableCategories, user, isSharedView, myMemberCategoryId]);

  const getCategory = (id) => displayCategories.find((c) => c.id === id) || displayCategories[0];

  // --- Title suggestions ------------------------------------------------
  // Typing a title offers earlier events in this calendar with a matching
  // name; picking one copies its label, all-day flag and times into the
  // *new* event so a recurring appointment is two taps instead of a form.
  // The source event is never touched — it is a template, not a link.
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  const titleSuggestions = useMemo(
    () => (editingEvent ? [] : suggestEvents(calendarEvents, newEvent.title)),
    [calendarEvents, newEvent.title, editingEvent]
  );

  const applySuggestion = (ev) => {
    setNewEvent(applySuggestionTo(newEvent, ev));
    setSuggestionsOpen(false);
  };

  const suggestionMeta = (ev) => {
    const when = t('calendar.suggestLast', 'Zuletzt {date}', { date: fmt(ev.date, 'short') });
    const time = ev.allDay
      ? t('dashboard.allDay', 'ganztägig')
      : `${ev.startTime || ''}${ev.endTime ? '–' + ev.endTime : ''}`;
    return time ? `${when} · ${time}` : when;
  };

  const updateLabel = (id, name) => {
    const trimmed = name.trim();
    const scope = sharedCalendarId || null;
    const isCurrentScope = (c) => (c.sharedCalendarId || null) === scope;

    // Partition: current-scope rows get modified, everything else passes
    // through untouched so other calendars' labels aren't affected.
    const otherScope = allCategories.filter((c) => !isCurrentScope(c));
    const currentScope = allCategories.filter(isCurrentScope);
    const existing = currentScope.find((c) => c.id === id);

    let nextCurrent;
    if (!trimmed) {
      if (scope && existing) {
        // Shared view: clearing the name removes the row so the default
        // label (from `defaultData`) takes over for every member again.
        nextCurrent = currentScope.filter((c) => c.id !== id);
      } else if (!scope && existing) {
        const capitalizedId = id.charAt(0).toUpperCase() + id.slice(1);
        nextCurrent = currentScope.map((c) =>
          c.id === id ? { ...c, name: capitalizedId, isCustom: false } : c
        );
      } else {
        nextCurrent = currentScope;
      }
    } else if (existing) {
      nextCurrent = currentScope.map((c) =>
        c.id === id ? { ...c, name: trimmed, isCustom: true } : c
      );
    } else {
      // No row in this scope yet (shared view): create one using the default
      // color for this id as the template.
      const base =
        defaultData.calendar.categories.find((d) => d.id === id) || { id };
      nextCurrent = [
        ...currentScope,
        { ...base, name: trimmed, isCustom: true, sharedCalendarId: scope }
      ];
    }

    updateData({
      calendar: {
        ...data.calendar,
        categories: [...otherScope, ...nextCurrent]
      }
    });
  };

  // Build the calendar grid (6 weeks starting Monday). Parameterized by
  // `baseDate` so we can also render the previous/next month grids during
  // a swipe transition.
  const getWeeksForMonth = (baseDate) => {
    const monthStart = startOfMonth(baseDate);
    const gridStart = startOfWeek(monthStart, { weekStartsOn: 1 });
    // Always render exactly 6 weeks so adjacent months in the carousel
    // (which uses flex with align-items: stretch) have matching heights
    // and the current month doesn't get an empty row appended when a
    // neighbour is taller.
    const days = [];
    for (let i = 0; i < 42; i++) days.push(addDays(gridStart, i));
    const out = [];
    for (let i = 0; i < days.length; i += 7) out.push(days.slice(i, i + 7));
    return out;
  };

  // Expand recurring events into concrete instances for a 3-month window
  // (prev/current/next) so the carousel swipe can reveal real data on the
  // adjacent months instead of empty grids.
  const expandedCalendarEvents = useMemo(() => {
    const prevStart = startOfWeek(startOfMonth(subMonths(currentDate, 1)), { weekStartsOn: 1 });
    const nextEnd = endOfWeek(endOfMonth(addMonths(currentDate, 1)), { weekStartsOn: 1 });
    return expandEvents(calendarEvents, addDays(prevStart, -7), addDays(nextEnd, 7));
  }, [calendarEvents, currentDate]);

  const events = useMemo(
    () => [...expandedCalendarEvents, ...birthdayEvents, ...todoEvents],
    [expandedCalendarEvents, birthdayEvents, todoEvents]
  );

  const getEventsForDay = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return events
      .filter((ev) => {
        if (ev.endDate && ev.endDate !== ev.date) {
          return dateStr >= ev.date && dateStr <= ev.endDate;
        }
        return ev.date === dateStr;
      })
      .sort((a, b) => (a.startTime || '').localeCompare(b.startTime || ''));
  };

  // Multi-day all-day events spanning across a week row, with stacking
  const getMultiDayBars = (week) => {
    const weekStartStr = format(week[0], 'yyyy-MM-dd');
    const weekEndStr = format(week[6], 'yyyy-MM-dd');

    const candidates = events
      .filter(
        (ev) =>
          ev.allDay !== false &&
          ev.endDate &&
          ev.endDate !== ev.date &&
          ev.date <= weekEndStr &&
          ev.endDate >= weekStartStr
      )
      .map((ev) => {
        const startIdx = week.findIndex((d) => format(d, 'yyyy-MM-dd') >= ev.date);
        const endIdxRev = [...week].reverse().findIndex((d) => format(d, 'yyyy-MM-dd') <= ev.endDate);
        const endIdx = 6 - endIdxRev;
        return {
          event: ev,
          startCol: Math.max(0, startIdx),
          endCol: Math.min(6, endIdx),
          continuesLeft: ev.date < weekStartStr,
          continuesRight: ev.endDate > weekEndStr
        };
      })
      .sort((a, b) => {
        // Longer events first, then by start date
        const lenA = a.endCol - a.startCol;
        const lenB = b.endCol - b.startCol;
        if (lenB !== lenA) return lenB - lenA;
        return a.startCol - b.startCol;
      });

    // Assign stacking row
    const rowEnds = []; // rowEnds[row] = last occupied col
    return candidates.map((bar) => {
      let row = 0;
      while (rowEnds[row] !== undefined && rowEnds[row] >= bar.startCol) row++;
      rowEnds[row] = bar.endCol;
      return { ...bar, row };
    });
  };

  // Compare old vs new event payloads. Returns the list of changed
  // logical fields (used for the audit log summary), or [] if nothing
  // meaningful changed.
  const diffEventFields = (oldEv, newEv) => {
    const changed = [];
    if ((oldEv.title || '') !== (newEv.title || '')) changed.push('title');
    const dateChanged =
      oldEv.date !== newEv.date ||
      (oldEv.endDate || oldEv.date) !== (newEv.endDate || newEv.date) ||
      (oldEv.startTime || '') !== (newEv.startTime || '') ||
      (oldEv.endTime || '') !== (newEv.endTime || '') ||
      !!oldEv.allDay !== !!newEv.allDay;
    if (dateChanged) changed.push('date');
    if ((oldEv.categoryId || '') !== (newEv.categoryId || '')) changed.push('label');
    if ((oldEv.notes || '') !== (newEv.notes || '')) changed.push('notes');
    if (
      JSON.stringify(oldEv.participantIds || []) !==
      JSON.stringify(newEv.participantIds || [])
    )
      changed.push('participants');
    if (
      JSON.stringify(oldEv.recurrence || null) !==
      JSON.stringify(newEv.recurrence || null)
    )
      changed.push('recurrence');
    return changed;
  };

  const handleSaveEvent = () => {
    if (!newEvent.title.trim() || !newEvent.date) return;
    const eventData = {
      ...newEvent,
      title: newEvent.title.trim(),
      endDate: newEvent.endDate || newEvent.date,
      recurrence: newEvent.recurrence || null,
      linkedSportName: isSharedView ? null : (newEvent.linkedSportName || null),
      participantIds: isSharedView ? (newEvent.participantIds || []) : [],
      sharedCalendarId: sharedCalendarId || null
    };

    const now = new Date().toISOString();
    const me = user?.id || null;
    if (editingEvent) {
      const baseId = editingEvent.originalId || editingEvent.id;
      // Map over ALL events — the edit modal can be reached from the
      // detail view of an event outside the current calendar scope
      // (activity feed); mapping the scoped list would silently drop the
      // edit. `sharedCalendarId` below keeps the event in its own calendar.
      const updatedEvents = allCalendarEvents.map((ev) => {
        if (ev.id !== baseId) return ev;
        const fields = diffEventFields(ev, eventData);
        const audit = Array.isArray(ev.audit) ? ev.audit : [];
        const newAudit =
          fields.length > 0
            ? [
                ...audit,
                {
                  id: `${now}-${Math.random().toString(36).slice(2, 7)}`,
                  type: 'updated',
                  userId: me,
                  at: now,
                  fields
                }
              ]
            : audit;
        return {
          ...ev,
          ...eventData,
          audit: newAudit,
          sharedCalendarId:
            ev.sharedCalendarId || sharedCalendarId || null
        };
      });
      commitAllEvents(updatedEvents);
    } else {
      commitEvents([
        ...calendarEvents,
        {
          id: Date.now().toString(),
          ...eventData,
          audit: [
            {
              id: `${now}-create`,
              type: 'created',
              userId: me,
              at: now
            }
          ],
          comments: []
        }
      ]);
    }
    closeModal();
  };

  // Append a chat comment to the currently-viewed event.
  const handleAddComment = () => {
    const text = commentDraft.trim();
    if (!text || !viewingEventId) return;
    const me = user?.id || null;
    const now = new Date().toISOString();
    // Map over ALL events, not the scoped list — the detail view can show
    // an event from another calendar (via the activity feed).
    const updated = allCalendarEvents.map((ev) => {
      if (ev.id !== viewingEventId) return ev;
      const comments = Array.isArray(ev.comments) ? ev.comments : [];
      return {
        ...ev,
        comments: [
          ...comments,
          {
            id: `${now}-${Math.random().toString(36).slice(2, 7)}`,
            userId: me,
            text,
            at: now
          }
        ]
      };
    });
    commitAllEvents(updated);
    setCommentDraft('');
  };

  const handleDeleteEvent = async (id) => {
    const baseId = (editingEvent && editingEvent.originalId) || id;
    const target = allCalendarEvents.find((e) => e.id === baseId);
    const isSeries = target && target.recurrence && target.recurrence.freq;
    if (isSeries) {
      // Open 3-option dialog
      const occurrenceDate =
        (editingEvent && editingEvent.clickedOccurrenceDate) || target.date;
      setRecurringDeleteTarget({ baseId, occurrenceDate });
      return;
    }
    if (!(await confirm({ message: 'Event löschen?', danger: true }))) return;
    commitAllEvents(allCalendarEvents.filter((ev) => ev.id !== baseId));
    closeModal();
  };

  // Recurring deletion handlers. These operate on the full event list so
  // they also work for events opened from outside the current calendar
  // scope (activity feed → detail view → delete).
  const deleteOnlyThisOccurrence = () => {
    const { baseId, occurrenceDate } = recurringDeleteTarget;
    const target = allCalendarEvents.find((e) => e.id === baseId);
    if (!target) return;
    if (target.date === occurrenceDate) {
      // Deleting the very first occurrence: shift series start to next occurrence
      const nextRange = addDays(parseISO(occurrenceDate), 1);
      const future = expandEvents([target], nextRange, addDays(nextRange, 365 * 5));
      const nextOcc = future.find((o) => o.date > occurrenceDate);
      if (!nextOcc) {
        // No future occurrences -> delete entire series
        commitAllEvents(allCalendarEvents.filter((e) => e.id !== baseId));
      } else {
        const newDate = nextOcc.date;
        const dur = parseISO(target.endDate || target.date).getTime() - parseISO(target.date).getTime();
        const newEndDate = format(new Date(parseISO(newDate).getTime() + dur), 'yyyy-MM-dd');
        const updated = {
          ...target,
          date: newDate,
          endDate: newEndDate
        };
        commitAllEvents(allCalendarEvents.map((e) => (e.id === baseId ? updated : e)));
      }
    } else {
      const exceptions = [...(target.recurrence.exceptions || []), occurrenceDate];
      const updated = {
        ...target,
        recurrence: { ...target.recurrence, exceptions }
      };
      commitAllEvents(allCalendarEvents.map((e) => (e.id === baseId ? updated : e)));
    }
    setRecurringDeleteTarget(null);
    closeModal();
  };

  const deleteThisAndFuture = () => {
    const { baseId, occurrenceDate } = recurringDeleteTarget;
    const target = allCalendarEvents.find((e) => e.id === baseId);
    if (!target) return;
    if (target.date === occurrenceDate) {
      // Truncating from the very start = delete the whole series
      commitAllEvents(allCalendarEvents.filter((e) => e.id !== baseId));
    } else {
      const newUntil = format(addDays(parseISO(occurrenceDate), -1), 'yyyy-MM-dd');
      const updated = {
        ...target,
        recurrence: {
          ...target.recurrence,
          endType: 'until',
          until: newUntil,
          count: undefined,
          custom: target.recurrence.custom || true
        }
      };
      commitAllEvents(allCalendarEvents.map((e) => (e.id === baseId ? updated : e)));
    }
    setRecurringDeleteTarget(null);
    closeModal();
  };

  const deleteEntireSeries = () => {
    const { baseId } = recurringDeleteTarget;
    commitAllEvents(allCalendarEvents.filter((e) => e.id !== baseId));
    setRecurringDeleteTarget(null);
    closeModal();
  };

  const closeModal = () => {
    setShowAddEvent(false);
    setEditingEvent(null);
    setNewEvent({
      title: '',
      date: format(selectedDate || new Date(), 'yyyy-MM-dd'),
      endDate: '',
      startTime: '',
      endTime: '',
      allDay: false,
      categoryId: lastUsedCategoryId,
      notes: '',
      recurrence: null,
      linkedSportName: null,
      participantIds: isSharedView && user ? [user.id] : []
    });
  };

  const openAddEvent = (date) => {
    setSelectedDate(date);
    setNewEvent({
      title: '',
      date: format(date, 'yyyy-MM-dd'),
      endDate: '',
      startTime: '',
      endTime: '',
      allDay: false,
      categoryId: lastUsedCategoryId,
      notes: '',
      recurrence: null,
      linkedSportName: null,
      participantIds: isSharedView && user ? [user.id] : []
    });
    setShowAddEvent(true);
  };

  // `occurrenceDate` carries the tapped occurrence when the caller only has
  // the base series row (detail view) — without it, `event.date` would be
  // the series START and occurrence-scoped deletes would hit the wrong one.
  const openEditEvent = (event, occurrenceDate = null) => {
    // If clicking an expanded occurrence, edit the base series
    const baseId = event.originalId || event.id;
    const base = allCalendarEvents.find((e) => e.id === baseId) || event;
    setEditingEvent({
      ...base,
      originalId: baseId,
      // the occurrence the user actually clicked
      clickedOccurrenceDate: occurrenceDate || event.date
    });
    setNewEvent({
      title: base.title,
      date: base.date,
      endDate: base.endDate || base.date,
      startTime: base.startTime || '',
      endTime: base.endTime || '',
      allDay: base.allDay !== false,
      categoryId: base.categoryId || lastUsedCategoryId,
      notes: base.notes || '',
      recurrence: base.recurrence || null,
      linkedSportName: base.linkedSportName || null,
      participantIds: Array.isArray(base.participantIds)
        ? base.participantIds
        : base.createdBy
        ? [base.createdBy]
        : []
    });
    setShowAddEvent(true);
  };

  // Acting on a widget tap: jump to the month, optionally open that day's
  // panel or the new-event form. Keyed on the intent's nonce so tapping the
  // same day twice works.
  const intentNonceRef = useRef(null);
  useEffect(() => {
    if (!intent || intent.nonce === intentNonceRef.current) return;
    intentNonceRef.current = intent.nonce;
    const value = intent.value || '';
    const day = /^\d{4}-\d{2}-\d{2}$/.test(value) ? parseISO(value) : null;
    const month = /^\d{4}-\d{2}$/.test(value) ? parseISO(`${value}-01`) : null;
    const target = day || month || new Date();
    setCurrentDate(target);
    if (intent.action === 'day' && day) {
      setSelectedDate(day);
      setDayPanelDate(day);
    } else if (intent.action === 'new') {
      setSelectedDate(day || target);
      openAddEvent(day || target);
    } else {
      setSelectedDate(null);
      setDayPanelDate(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intent]);

  // Month/year lists for the wheel-style picker.
  const monthLabels = useMemo(
    () => Array.from({ length: 12 }, (_, m) => fmt(new Date(2000, m, 1), 'month')),
    [lang]
  );
  const yearOptions = useMemo(() => {
    const base = getYear(new Date());
    const out = [];
    for (let y = base - 20; y <= base + 20; y++) out.push(y);
    return out;
  }, []);

  // Same source as the home-screen widget, so the two can't drift apart.
  const weekdayLabels = useMemo(() => localeWeekdays(), [lang]);

  // Shared-calendar birthday sub-view: reuse the standalone BirthdayCalendar
  // with a `sharedCalendarId` so reads/writes are scoped to this calendar.
  if (
    showSharedBirthdays &&
    isSharedView &&
    currentSharedCalendar &&
    currentSharedCalendar.hasBirthdays
  ) {
    return (
      <BirthdayCalendar
        data={data}
        updateData={updateData}
        sharedCalendarId={sharedCalendarId}
        onBack={() => setShowSharedBirthdays(false)}
      />
    );
  }

  return (
    <div className="space-y-3">
      {/* Header — month picker + actions on a single line, no wrap.
          Hidden when the user has switched the bottom toggle to "Activity"
          so the feed has the full screen. */}
      {viewMode === 'calendar' && (
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-3 min-w-0">
          {/* Month/Year selector — iOS-style dual wheel picker */}
          <div className="relative min-w-0">
            <button
              onClick={() => setShowMonthPicker(!showMonthPicker)}
              className="flex items-center gap-1 sm:gap-2 text-lg sm:text-3xl font-bold text-gray-900 hover:text-primary-600 transition-colors truncate"
            >
              <span className="truncate">
                {fmt(currentDate, 'monthYear')}
              </span>
              <ChevronRight
                size={20}
                className={`flex-shrink-0 transition-transform ${showMonthPicker ? 'rotate-90' : ''}`}
              />
            </button>
            {showMonthPicker && (
              <>
                {/* Click-away backdrop */}
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setShowMonthPicker(false)}
                  aria-hidden="true"
                />
                <WheelPicker
                  months={monthLabels}
                  years={yearOptions}
                  selectedMonth={currentDate.getMonth()}
                  selectedYear={currentDate.getFullYear()}
                  onChange={(m, y) => {
                    setCurrentDate((d) => setYear(setMonth(d, m), y));
                  }}
                />
              </>
            )}
          </div>
        </div>

        <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0 justify-end">
          {!isSharedView && (
            <button
              onClick={() => {
                setNewSharedName('');
                setShowCreateShared(true);
              }}
              className="btn-secondary !px-2 !py-1.5 sm:!px-4 sm:!py-2 flex items-center gap-2"
              title={t('sharedCalendar.createButton')}
            >
              <UsersIcon size={16} className="sm:hidden" />
              <UsersIcon size={18} className="hidden sm:inline-block" />
              <span className="hidden sm:inline">{t('sharedCalendar.createButton')}</span>
            </button>
          )}
          {isSharedView && currentSharedCalendar && currentSharedCalendar.hasBirthdays && (
            <button
              onClick={() => setShowSharedBirthdays(true)}
              className="btn-secondary !px-2 !py-1.5 sm:!px-4 sm:!py-2 flex items-center gap-2"
              title={t('sharedCalendar.manageBirthdays')}
            >
              <Cake size={16} className="sm:hidden" />
              <Cake size={18} className="hidden sm:inline-block" />
              <span className="hidden sm:inline">{t('sharedCalendar.birthdays')}</span>
            </button>
          )}
          {isSharedView && currentSharedCalendar && (
            <button
              onClick={async () => {
                setSharedSettings({ loading: true, members: [], inviteUrl: null, copied: false });
                try {
                  const [members, inviteUrl] = await Promise.all([
                    listSharedCalendarMembers(sharedCalendarId),
                    currentSharedCalendar.isOwner
                      ? getInviteUrl(sharedCalendarId)
                      : Promise.resolve(null)
                  ]);
                  setSharedSettings({ loading: false, members, inviteUrl, copied: false });
                  // Refresh the avatar/category lookup map too so the
                  // member-color dots update immediately after they were
                  // changed in another session.
                  const map = {};
                  for (const m of members) map[m.userId] = m;
                  setMembersById(map);
                } catch (err) {
                  setSharedSettings(null);
                  window.alert(err.message || 'Failed to load calendar settings');
                }
              }}
              className="btn-secondary !px-2 !py-1.5 sm:!px-4 sm:!py-2 flex items-center gap-2"
              title={t('sharedCalendar.settings')}
              aria-label={t('sharedCalendar.settings')}
            >
              <SettingsIcon size={16} className="sm:hidden" />
              <SettingsIcon size={18} className="hidden sm:inline-block" />
            </button>
          )}
          <button
            onClick={() => setShowLabelManager(true)}
            className="btn-secondary !px-2 !py-1.5 sm:!px-4 sm:!py-2 flex items-center gap-2"
            title="Labels verwalten"
          >
            <Tags size={16} className="sm:hidden" />
            <Tags size={18} className="hidden sm:inline-block" />
            <span className="hidden sm:inline">Labels</span>
          </button>
          <button
            onClick={() => openAddEvent(selectedDate || currentDate)}
            className="hidden sm:flex btn bg-primary-600 text-white hover:bg-primary-700 items-center gap-2"
          >
            <Plus size={18} />
            Event
          </button>
        </div>
      </div>
      )}

      {/* Create shared calendar modal */}
      {showCreateShared && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {t('sharedCalendar.createTitle')}
              </h3>
              <button
                onClick={() => setShowCreateShared(false)}
                className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded"
              >
                <X size={18} />
              </button>
            </div>
            <input
              type="text"
              autoFocus
              className="input w-full"
              placeholder={t('sharedCalendar.namePlaceholder')}
              value={newSharedName}
              onChange={(e) => setNewSharedName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && newSharedName.trim() && !creatingShared) {
                  e.preventDefault();
                  (async () => {
                    setCreatingShared(true);
                    try {
                      await createSharedCalendar(newSharedName.trim(), {
                        withBirthdays: newSharedWithBirthdays
                      });
                      if (reload) await reload();
                      setShowCreateShared(false);
                      setNewSharedName('');
                      setNewSharedWithBirthdays(false);
                    } catch (err) {
                      window.alert(err.message || 'Failed to create');
                    } finally {
                      setCreatingShared(false);
                    }
                  })();
                }
              }}
            />
            <label className="mt-4 flex items-start gap-3 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={newSharedWithBirthdays}
                onChange={(e) => setNewSharedWithBirthdays(e.target.checked)}
                className="mt-0.5 h-4 w-4 accent-primary-600"
              />
              <span className="text-sm">
                <span className="font-medium text-gray-800 dark:text-gray-100">
                  {t('sharedCalendar.withBirthdays')}
                </span>
                <span className="block text-xs text-gray-500 dark:text-gray-400">
                  {t('sharedCalendar.withBirthdaysHint')}
                </span>
              </span>
            </label>
            <div className="mt-4 flex justify-end gap-2">
              <button
                onClick={() => setShowCreateShared(false)}
                className="btn-secondary"
                disabled={creatingShared}
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={async () => {
                  if (!newSharedName.trim()) return;
                  setCreatingShared(true);
                  try {
                    await createSharedCalendar(newSharedName.trim(), {
                      withBirthdays: newSharedWithBirthdays
                    });
                    if (reload) await reload();
                    setShowCreateShared(false);
                    setNewSharedName('');
                    setNewSharedWithBirthdays(false);
                  } catch (err) {
                    window.alert(err.message || 'Failed to create');
                  } finally {
                    setCreatingShared(false);
                  }
                }}
                disabled={creatingShared || !newSharedName.trim()}
                className="btn bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"
              >
                {t('sharedCalendar.create')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Shared calendar settings modal */}
      {sharedSettings && currentSharedCalendar && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-xl w-full max-w-md p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {t('sharedCalendar.settings')}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5 truncate">
                  {currentSharedCalendar.name}
                </p>
              </div>
              <button
                onClick={() => setSharedSettings(null)}
                className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded"
              >
                <X size={18} />
              </button>
            </div>

            {/* My label in this calendar — defaults the colour for any
                event you create here (Quick-Add and the modal). */}
            <div className="mb-4">
              <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                Dein Label in diesem Kalender
              </div>
              <div className="flex items-center gap-2 overflow-x-auto no-scrollbar -mx-1 px-1 pb-1">
                {selectableCategories.map((c) => {
                  const myCat =
                    (user && membersById[user.id]?.categoryId) || null;
                  const active = myCat === c.id;
                  return (
                    <button
                      key={c.id}
                      type="button"
                      onClick={async () => {
                        try {
                          await setMyCalendarCategory(
                            sharedCalendarId,
                            active ? null : c.id
                          );
                          // Optimistic local update so the dot reflects
                          // immediately; the next fetch reconciles.
                          setMembersById((prev) => {
                            if (!user) return prev;
                            const me = prev[user.id] || { userId: user.id };
                            return {
                              ...prev,
                              [user.id]: {
                                ...me,
                                categoryId: active ? null : c.id
                              }
                            };
                          });
                        } catch (err) {
                          toast.error(err.message || 'Fehler');
                        }
                      }}
                      className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-medium transition-colors ${
                        active
                          ? 'border-transparent text-white'
                          : 'border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800'
                      }`}
                      style={
                        active ? { backgroundColor: c.color } : undefined
                      }
                    >
                      <span
                        className="w-2.5 h-2.5 rounded-full"
                        style={{ backgroundColor: c.color }}
                      />
                      {c.name}
                    </button>
                  );
                })}
              </div>
              <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                Wird automatisch verwendet, wenn du hier ein neues Ereignis
                erstellst.
              </p>
            </div>

            {/* Members */}
            <div>
              <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                {t('sharedCalendar.membersTitle')}
              </div>
              {sharedSettings.loading ? (
                <div className="text-sm text-gray-500 dark:text-gray-400 py-2">
                  {t('sharedCalendar.loadingMembers')}
                </div>
              ) : (
                <ul className="divide-y divide-gray-200 dark:divide-gray-800 border border-gray-200 dark:border-gray-800 rounded-lg overflow-hidden">
                  {sharedSettings.members.map((m) => {
                    const isMe = user && m.userId === user.id;
                    const color =
                      AVATAR_COLORS.find((c) => c.id === m.avatarColor) ||
                      AVATAR_COLORS[0];
                    const initial = (m.displayName || m.email || '?')
                      .trim()
                      .charAt(0)
                      .toUpperCase();
                    return (
                      <li
                        key={m.userId}
                        className="flex items-center gap-3 px-3 py-2.5 bg-white dark:bg-gray-900"
                      >
                        {m.avatarKind === 'photo' && m.avatarUrl ? (
                          <img
                            src={m.avatarUrl}
                            alt=""
                            className="flex-shrink-0 w-9 h-9 rounded-full object-cover shadow-sm"
                          />
                        ) : (
                          <div
                            className="flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-semibold shadow-sm"
                            style={{ backgroundColor: color.swatch }}
                          >
                            {m.avatarEmoji ? (
                              <span className="text-lg leading-none">{m.avatarEmoji}</span>
                            ) : (
                              initial
                            )}
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate flex items-center gap-1.5">
                            {m.categoryId &&
                              (() => {
                                const memberCat = selectableCategories.find(
                                  (c) => c.id === m.categoryId
                                );
                                if (!memberCat) return null;
                                return (
                                  <span
                                    className="inline-block w-2.5 h-2.5 rounded-full flex-shrink-0"
                                    style={{ backgroundColor: memberCat.color }}
                                    title={memberCat.name}
                                  />
                                );
                              })()}
                            <span className="truncate">
                              {m.displayName || m.userId.slice(0, 8)}
                            </span>
                            {isMe && (
                              <span className="ml-1 text-xs text-gray-400 flex-shrink-0">
                                ({t('sharedCalendar.you')})
                              </span>
                            )}
                          </div>
                        </div>
                        <span
                          className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${
                            m.isOwner
                              ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200'
                              : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300'
                          }`}
                        >
                          {m.isOwner && <Crown size={12} />}
                          {m.isOwner ? t('sharedCalendar.owner') : t('sharedCalendar.member')}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            {/* Invite link (owner only) */}
            {currentSharedCalendar.isOwner && sharedSettings.inviteUrl && (
              <div className="mt-5">
                <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                  {t('sharedCalendar.invite')}
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                  {t('sharedCalendar.shareHint')}
                </p>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    readOnly
                    className="input flex-1 font-mono text-xs"
                    value={sharedSettings.inviteUrl}
                    onFocus={(e) => e.target.select()}
                  />
                  <button
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(sharedSettings.inviteUrl);
                        setSharedSettings((s) => (s ? { ...s, copied: true } : s));
                        setTimeout(
                          () => setSharedSettings((s) => (s ? { ...s, copied: false } : s)),
                          1800
                        );
                      } catch (_) {}
                    }}
                    className="btn-secondary flex items-center gap-2"
                  >
                    <Copy size={16} />
                    {sharedSettings.copied ? t('sharedCalendar.copied') : t('sharedCalendar.copyLink')}
                  </button>
                </div>
              </div>
            )}

            {/* Danger zone */}
            <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-800 flex flex-wrap gap-2 justify-end">
              {!currentSharedCalendar.isOwner && (
                <button
                  onClick={async () => {
                    if (!(await confirm({ message: t('sharedCalendar.leaveConfirm'), danger: true }))) return;
                    try {
                      await leaveSharedCalendar(sharedCalendarId);
                      setSharedSettings(null);
                      if (reload) await reload();
                    } catch (err) {
                      window.alert(err.message || 'Failed to leave');
                    }
                  }}
                  className="btn-secondary flex items-center gap-2 text-red-600"
                >
                  <LogOut size={16} />
                  {t('sharedCalendar.leave')}
                </button>
              )}
              {currentSharedCalendar.isOwner && (
                <button
                  onClick={async () => {
                    if (!(await confirm({ message: t('sharedCalendar.deleteConfirm'), danger: true }))) return;
                    try {
                      await deleteSharedCalendar(sharedCalendarId);
                      setSharedSettings(null);
                      if (reload) await reload();
                    } catch (err) {
                      window.alert(err.message || 'Failed to delete');
                    }
                  }}
                  className="btn-secondary flex items-center gap-2 text-red-600"
                >
                  <Trash2 size={16} />
                  {t('sharedCalendar.delete')}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Day events modal — slides up from the bottom, full viewport
          height with a narrow width (centered horizontally), so it covers
          the calendar without going edge-to-edge. */}
      {dayPanelDate && (() => {
        const items = getEventsForDay(dayPanelDate);
        return (
          <div
            className="fixed inset-0 z-40 flex items-end justify-center bg-black/40"
            onClick={() => setDayPanelDate(null)}
          >
            <div
              // Leave the status bar clear: a full 100vh sheet slid its own
              // header up behind the clock and the blur strip.
              className={`bg-white w-full max-w-md h-[calc(100dvh-env(safe-area-inset-top)-0.5rem)] sm:h-[90vh] sm:my-4 rounded-t-2xl sm:rounded-2xl shadow-2xl flex flex-col overflow-hidden ${
                dayModalDy === 0 && !dayModalAnim ? 'animate-slide-up' : ''
              }`}
              onClick={(e) => e.stopPropagation()}
              style={{
                transform: `translateY(${dayModalDy}px)`,
                transition: dayModalAnim ? 'transform 220ms ease-out' : 'none',
                willChange: 'transform'
              }}
              onTouchStart={(e) => {
                // Record the start of a potential drag. We'll only engage
                // it on the first move if the inner list is at the top
                // and the user is moving downward — otherwise we leave
                // the touch alone so normal scrolling can happen.
                const t = e.touches[0];
                dayModalDragRef.current = {
                  y: t.clientY,
                  startScrollTop: dayModalScrollRef.current?.scrollTop || 0,
                  mode: 'pending'
                };
                setDayModalAnim(false);
              }}
              onTouchMove={(e) => {
                const s = dayModalDragRef.current;
                if (!s) return;
                const dy = e.touches[0].clientY - s.y;
                if (s.mode === 'pending') {
                  // Only first significant movement decides the gesture.
                  if (Math.abs(dy) < 5) return;
                  if (dy > 0 && s.startScrollTop <= 0) {
                    s.mode = 'dragging';
                  } else {
                    s.mode = 'scrolling';
                  }
                }
                if (s.mode === 'dragging') {
                  setDayModalDy(Math.max(0, dy));
                }
              }}
              onTouchEnd={() => {
                const s = dayModalDragRef.current;
                dayModalDragRef.current = null;
                if (!s || s.mode !== 'dragging') return;
                const dy = dayModalDy;
                setDayModalAnim(true);
                if (dy > 120) {
                  // Animate off-screen, then close.
                  setDayModalDy(window.innerHeight);
                  setTimeout(() => {
                    setDayPanelDate(null);
                    setDayModalDy(0);
                    setDayModalAnim(false);
                  }, 220);
                } else {
                  setDayModalDy(0);
                  setTimeout(() => setDayModalAnim(false), 220);
                }
              }}
            >
            {/* Drag handle — visual affordance only; the entire panel is
                draggable via the touch handlers above. */}
            <div className="flex-shrink-0 pt-2 pb-1 flex justify-center">
              <div className="w-10 h-1.5 rounded-full bg-gray-300" />
            </div>
            <div className="flex items-center justify-between px-4 py-2 bg-gray-50 border-b border-gray-200 flex-shrink-0">
              <h3 className="font-semibold text-gray-900 truncate">
                {fmt(dayPanelDate, 'full')}
              </h3>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => openAddEvent(dayPanelDate)}
                  className="text-primary-600 hover:text-primary-700 text-sm font-medium flex items-center gap-1 px-2 py-1"
                >
                  <Plus size={16} /> Event
                </button>
                <button
                  onClick={() => setDayPanelDate(null)}
                  className="p-1 hover:bg-gray-100 rounded"
                  aria-label="Close"
                >
                  <X size={18} />
                </button>
              </div>
            </div>
            {items.length === 0 ? (
              <div className="flex-1 flex items-center justify-center px-4 py-8 text-center text-gray-500 text-sm">
                Nichts geplant.
              </div>
            ) : (
              <div ref={dayModalScrollRef} className="divide-y divide-gray-100 px-4 overflow-y-auto flex-1">
                {items.map((ev) => {
                  const cat = getCategory(ev.categoryId);
                  return (
                    <div
                      key={ev.id}
                      onClick={() => {
                        if (!ev.isBirthday && !ev.isTodo) {
                          // Open the detail view (read-only, with comments
                          // and history). Edit is reachable from there.
                          // Remember WHICH occurrence was tapped — for a
                          // recurring instance `ev.date` is the occurrence
                          // date while the base row's date is the series
                          // start.
                          setViewingEventId(ev.originalId || ev.id);
                          setViewingOccurrenceDate(ev.originalId ? ev.date : null);
                          setDayPanelDate(null);
                        }
                      }}
                      className={`flex items-stretch gap-3 py-3 ${
                        ev.isBirthday || ev.isTodo ? 'cursor-default' : 'hover:bg-gray-50 cursor-pointer'
                      }`}
                    >
                      <div className="flex flex-col items-end justify-center w-14 flex-shrink-0 text-xs font-medium leading-tight">
                        {ev.isBirthday ? (
                          <Gift size={18} style={{ color: cat.color }} />
                        ) : ev.isTodo ? (
                          <>
                            <CheckSquare size={18} style={{ color: cat.color }} />
                            {ev.startTime && (
                              <span className="text-gray-900 mt-0.5">{ev.startTime}</span>
                            )}
                          </>
                        ) : ev.allDay ? (
                          <span className="text-gray-500">Ganztägig</span>
                        ) : (
                          <>
                            <span className="text-gray-900">{ev.startTime || '—'}</span>
                            {ev.endTime && (
                              <span className="text-gray-400 mt-0.5">{ev.endTime}</span>
                            )}
                          </>
                        )}
                      </div>
                      <div
                        className="w-1 rounded-full flex-shrink-0"
                        style={{ backgroundColor: cat.color }}
                      />
                      <div className="flex-1 min-w-0 flex flex-col justify-center">
                        <div className="flex items-center gap-1.5 min-w-0">
                          <div
                            className={`font-semibold truncate flex-1 min-w-0 ${
                              ev.isTodo && ev.done
                                ? 'line-through text-gray-400'
                                : 'text-gray-900'
                            }`}
                          >
                            {ev.title}
                          </div>
                          {priorityEmoji(ev.priority) && (
                            <span className="flex-shrink-0 leading-none pr-0.5">
                              {priorityEmoji(ev.priority)}
                            </span>
                          )}
                        </div>
                        {ev.notes && (
                          <div className="text-xs text-gray-500 truncate mt-0.5">{ev.notes}</div>
                        )}
                      </div>
                      {isSharedView && !ev.isBirthday && (() => {
                        const ids = (Array.isArray(ev.participantIds) && ev.participantIds.length > 0)
                          ? ev.participantIds
                          : ev.createdBy ? [ev.createdBy] : [];
                        if (ids.length === 0) return null;
                        return (
                          <div className="flex items-center flex-shrink-0 -space-x-2">
                            {ids.slice(0, 4).map((uid) => (
                              <div key={uid} className="ring-2 ring-white dark:ring-gray-900 rounded-full">
                                {renderCreatorAvatar(uid, 28)}
                              </div>
                            ))}
                            {ids.length > 4 && (
                              <div className="w-7 h-7 rounded-full bg-gray-200 dark:bg-gray-700 text-xs font-semibold text-gray-700 dark:text-gray-200 flex items-center justify-center ring-2 ring-white dark:ring-gray-900">
                                +{ids.length - 4}
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  );
                })}
              </div>
            )}
            </div>
          </div>
        );
      })()}

      {/* Calendar Grid — edge-to-edge on mobile so each day column gets as
          much horizontal space as possible. On sm+ the usual rounded card
          style returns. The inner strip is a 3-wide carousel (prev /
          current / next) so horizontal swipes smoothly reveal the adjacent
          months under the finger. */}
      {viewMode === 'calendar' && (
      <div
        ref={carouselRef}
        className="bg-white border-y sm:border sm:rounded-xl border-gray-200 overflow-hidden -mx-4 sm:mx-0 touch-pan-y relative"
        onTouchStart={(e) => {
          const t = e.touches[0];
          const width = carouselRef.current?.offsetWidth || window.innerWidth;
          swipeRef.current = { x: t.clientX, y: t.clientY, width, locked: false };
          setSwipeAnim(false);
        }}
        onTouchMove={(e) => {
          const s = swipeRef.current;
          if (!s) return;
          const t = e.touches[0];
          const dx = t.clientX - s.x;
          const dy = t.clientY - s.y;
          // Lock direction on first significant movement. Only horizontal
          // drags drive the carousel; vertical ones let the page scroll.
          if (!s.locked) {
            if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return;
            s.locked = Math.abs(dx) > Math.abs(dy);
          }
          if (s.locked) {
            setSwipeActive(true);
            setSwipeDx(dx);
          }
        }}
        onTouchEnd={(e) => {
          const s = swipeRef.current;
          swipeRef.current = null;
          if (!s || !s.locked) {
            setSwipeActive(false);
            setSwipeDx(0);
            return;
          }
          const dx = swipeDx;
          const width = s.width || 1;
          const threshold = Math.min(80, width * 0.2);
          if (Math.abs(dx) > threshold) {
            // Animate to neighbour, then commit and reset with no transition.
            setSwipeAnim(true);
            setSwipeDx(dx < 0 ? -width : width);
            setTimeout(() => {
              setCurrentDate((d) => (dx < 0 ? addMonths(d, 1) : subMonths(d, 1)));
              setSwipeAnim(false);
              setSwipeActive(false);
              setSwipeDx(0);
            }, 220);
          } else {
            // Snap back.
            setSwipeAnim(true);
            setSwipeDx(0);
            setTimeout(() => {
              setSwipeAnim(false);
              setSwipeActive(false);
            }, 200);
          }
        }}
      >
        {(() => {
          const renderMonthGrid = (baseDate) => {
            const baseWeeks = getWeeksForMonth(baseDate);
            return (
              <div className="w-full flex-shrink-0">
                {/* Weekday header */}
                <div
                  className="grid border-b border-gray-200 bg-gray-50"
                  style={{
                    gridTemplateColumns: isMobile
                      ? 'repeat(7,minmax(0,1fr))'
                      : '50px repeat(7,minmax(0,1fr))'
                  }}
                >
                  {!isMobile && (
                    <div className="p-2 text-[10px] sm:text-xs font-medium text-gray-400 text-center">KW</div>
                  )}
                  {weekdayLabels.map((lbl, i) => (
                    <div
                      key={lbl}
                      className={`p-1.5 sm:p-2 text-[10px] sm:text-xs font-medium text-center uppercase tracking-wider ${
                        i >= 5 ? 'text-red-500' : 'text-gray-600'
                      }`}
                    >
                      {lbl}
                    </div>
                  ))}
                </div>

                {/* Weeks */}
                <div className="divide-y divide-gray-200">
                  {baseWeeks.map((week, wIdx) => {
            const multiDayBars = getMultiDayBars(week);
            const multiDayIds = new Set(multiDayBars.map((b) => b.event.id));
            const barRowCount = multiDayBars.reduce((m, b) => Math.max(m, b.row + 1), 0);
            const BAR_HEIGHT = isMobile ? 16 : 20; // px — match chip height on mobile
            const BAR_TOP_OFFSET = isMobile ? 26 : 32; // px below day number

            return (
              <div
                key={wIdx}
                className="grid min-h-[100px] sm:min-h-[120px] relative"
                style={{
                  gridTemplateColumns: isMobile
                    ? 'repeat(7,minmax(0,1fr))'
                    : '50px repeat(7,minmax(0,1fr))'
                }}
              >
                {!isMobile && (
                  <div className="flex items-start justify-center pt-2 bg-gray-50 text-xs font-medium text-gray-400">
                    {getWeek(week[0], { weekStartsOn: 1, firstWeekContainsDate: 4 })}
                  </div>
                )}

                {week.map((day, dayIdx) => {
                  const inMonth = isSameMonth(day, baseDate);
                  const today = isToday(day);
                  const weekend = day.getDay() === 0 || day.getDay() === 6;
                  const allDayEvents = getEventsForDay(day).filter((ev) => !multiDayIds.has(ev.id));
                  // Per-day reserved space: only the bars that actually cross
                  // THIS column's index push content down. Otherwise every
                  // day in the week would inherit gaps from bars on a
                  // different day (e.g. the 23rd or 30th).
                  const maxBarRowHere = multiDayBars.reduce(
                    (m, b) =>
                      dayIdx >= b.startCol && dayIdx <= b.endCol
                        ? Math.max(m, b.row + 1)
                        : m,
                    0
                  );
                  const dayReservedSpace = maxBarRowHere * BAR_HEIGHT;
                  const birthdayEventsForDay = allDayEvents.filter((ev) => ev.isBirthday);
                  const dayEvents = allDayEvents.filter((ev) => !ev.isBirthday);
                  const isSelected = selectedDate && isSameDay(day, selectedDate);
                  const firstBirthday = birthdayEventsForDay[0];
                  const firstBirthdayCat = firstBirthday ? getCategory(firstBirthday.categoryId) : null;

                  return (
                    <div
                      key={day.toISOString()}
                      onClick={() => {
                        // First tap selects the day. Tapping the
                        // already-selected day opens the day panel above
                        // the calendar.
                        if (selectedDate && isSameDay(day, selectedDate)) {
                          setDayPanelDate(day);
                        } else {
                          setSelectedDate(day);
                          setDayPanelDate(null);
                        }
                      }}
                      className={`border-l border-gray-200 p-1 sm:p-1.5 cursor-pointer transition-colors group min-w-0 overflow-hidden ${
                        inMonth ? 'bg-white' : 'bg-gray-50/50 opacity-40'
                      } ${isSelected ? 'ring-2 ring-primary-500 ring-inset' : ''} hover:bg-gray-50`}
                    >
                      <div className="flex items-center gap-1 mb-1 min-w-0">
                        <span
                          className={`flex-shrink-0 inline-flex items-center justify-center w-5 h-5 sm:w-6 sm:h-6 text-[11px] sm:text-xs font-medium rounded-full ${
                            today && popToday ? 'today-pop' : ''
                          } ${
                            today
                              ? 'bg-primary-600 text-white'
                              : !inMonth
                              ? 'text-gray-300'
                              : weekend
                              ? 'text-red-500'
                              : 'text-gray-700'
                          }`}
                        >
                          {format(day, 'd')}
                        </span>

                        {/* Birthday pill (inline next to day number) */}
                        {firstBirthday && firstBirthdayCat && (
                          <div
                            className="flex-1 min-w-0 text-xs px-1.5 py-0.5 rounded font-semibold flex items-center gap-1"
                            style={{
                              backgroundColor: `${firstBirthdayCat.color}22`,
                              color: firstBirthdayCat.color
                            }}
                            title={birthdayEventsForDay.map((b) => b.title).join(', ')}
                          >
                            <Gift size={12} className="flex-shrink-0" />
                            <span className="truncate">
                              {firstBirthday.title}
                              {birthdayEventsForDay.length > 1 && ` +${birthdayEventsForDay.length - 1}`}
                            </span>
                          </div>
                        )}

                      </div>

                      {/* Reserved space for multi-day bars that cross THIS day */}
                      {dayReservedSpace > 0 && (
                        <div style={{ height: dayReservedSpace }} aria-hidden="true" />
                      )}

                      {/* Events (single-day + timed multi-day) */}
                      <div className="space-y-0.5">
                        {dayEvents.slice(0, 4).map((ev) => {
                          const cat = getCategory(ev.categoryId);
                          const isAllDay = ev.allDay !== false;
                          if (ev.isTodo) {
                            // Todo chip — birthday-pill styling, but with the
                            // todo symbol; crossed off once the task is done.
                            return (
                              <div
                                key={ev.id}
                                className="text-[10px] sm:text-xs pl-1 sm:pl-1.5 pr-0.5 py-0.5 rounded font-medium leading-tight flex items-center gap-1 overflow-hidden"
                                style={{ backgroundColor: `${cat.color}22`, color: cat.color }}
                                title={ev.startTime ? `${ev.startTime} ${ev.title}` : ev.title}
                              >
                                <CheckSquare size={10} className="flex-shrink-0" />
                                <span
                                  className={`truncate flex-1 min-w-0 ${
                                    ev.done ? 'line-through opacity-60' : ''
                                  }`}
                                >
                                  {ev.title}
                                </span>
                                {priorityEmoji(ev.priority) && (
                                  <span className="flex-shrink-0 leading-none pr-0.5">
                                    {priorityEmoji(ev.priority)}
                                  </span>
                                )}
                              </div>
                            );
                          }
                          return (
                            <div
                              key={ev.id}
                              className="text-[10px] sm:text-xs px-1 sm:px-1.5 py-0.5 rounded font-medium cursor-pointer hover:opacity-80 leading-tight overflow-hidden"
                              style={
                                isAllDay
                                  ? { backgroundColor: cat.color, color: '#fff' }
                                  : { backgroundColor: `${cat.color}22`, color: cat.color }
                              }
                              title={ev.startTime ? `${ev.startTime} ${ev.title}` : ev.title}
                            >
                              <span className="fade-clip">{ev.title}</span>
                            </div>
                          );
                        })}
                        {dayEvents.length > 4 && (
                          <div className="text-xs text-gray-500 font-medium px-1">
                            +{dayEvents.length - 4} mehr
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}

                {/* Multi-day bars overlay */}
                {multiDayBars.length > 0 && (
                  <div
                    className="absolute pointer-events-none"
                    style={{
                      left: kwColWidth,
                      right: 0,
                      top: BAR_TOP_OFFSET,
                      height: barRowCount * BAR_HEIGHT
                    }}
                  >
                    {multiDayBars.map((bar) => {
                      const cat = getCategory(bar.event.categoryId);
                      const widthPct = ((bar.endCol - bar.startCol + 1) / 7) * 100;
                      const leftPct = (bar.startCol / 7) * 100;
                      return (
                        <div
                          key={bar.event.id}
                          className={`absolute text-[10px] sm:text-xs font-medium truncate px-1 sm:px-2 leading-tight pointer-events-none flex items-center justify-center ${
                            bar.continuesLeft ? '' : 'rounded-l'
                          } ${bar.continuesRight ? '' : 'rounded-r'}`}
                          style={{
                            left: `calc(${leftPct}% + 2px)`,
                            width: `calc(${widthPct}% - 4px)`,
                            top: bar.row * BAR_HEIGHT,
                            height: BAR_HEIGHT - 2,
                            backgroundColor: cat.color,
                            color: '#fff'
                          }}
                          title={bar.event.title}
                        >
                          {bar.event.title}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
                </div>
              </div>
            );
          };
          const prev = jumpTo && jumpBack ? jumpTo : subMonths(currentDate, 1);
          const next = jumpTo && !jumpBack ? jumpTo : addMonths(currentDate, 1);
          // Always render the 3-wide strip so the very first swipe doesn't
          // remount the grid mid-touch (which would drop the gesture).
          return (
            <div
              className="flex"
              style={{
                width: '300%',
                transform: `translateX(calc(-33.3333% + ${swipeDx}px))`,
                transition: swipeAnim ? 'transform 220ms ease-out' : 'none',
                willChange: 'transform'
              }}
            >
              <div className="w-1/3 flex-shrink-0">{renderMonthGrid(prev)}</div>
              <div className="w-1/3 flex-shrink-0">{renderMonthGrid(currentDate)}</div>
              <div className="w-1/3 flex-shrink-0">{renderMonthGrid(next)}</div>
            </div>
          );
        })()}
      </div>
      )}

      {/* Activity feed — chronological list of every recent change/comment
          across the events the user can see. Rendered when the bottom
          toggle is set to "Activity". */}
      {viewMode === 'activity' && (
        <div className="space-y-3 pb-24">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-gray-100">
            Aktivität
          </h2>
          {activityFeed.length === 0 ? (
            <div className="text-center py-16 text-gray-500 dark:text-gray-400">
              <Bell size={32} className="mx-auto mb-3 opacity-50" />
              <p className="text-sm">Noch keine Aktivität.</p>
              <p className="text-xs mt-1 opacity-70">
                Änderungen und Kommentare in geteilten Kalendern erscheinen hier.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {activityFeed.map(({ event: ev, sharedCalendar, entries }) => {
                const cat = getCategory(ev.categoryId);
                const color = (cat && cat.color) || '#6B7280';
                const fieldLabelDe = (f) => {
                  switch (f) {
                    case 'title': return 'Titel';
                    case 'date': return 'Datum';
                    case 'label': return 'Label';
                    case 'notes': return 'Notizen';
                    case 'participants': return 'Teilnehmer';
                    case 'recurrence': return 'Wiederholung';
                    default: return f;
                  }
                };
                const auditSummaryDe = (a) => {
                  if (a.type === 'created') return 'Ereignis erstellt';
                  const fields = a.fields || [];
                  if (fields.length === 0) return 'Ereignis aktualisiert';
                  if (fields.length === 1) return `${fieldLabelDe(fields[0])} aktualisiert`;
                  return `${fields.map(fieldLabelDe).join(', ')} aktualisiert`;
                };
                const fmtRel = (iso) => {
                  if (!iso) return '';
                  try {
                    const d = parseISO(iso);
                    const today = new Date();
                    const diffDays = Math.floor(
                      (new Date(today).setHours(0, 0, 0, 0) -
                        new Date(d).setHours(0, 0, 0, 0)) /
                        86400000
                    );
                    if (diffDays === 0)
                      return `${t('common.today', 'Heute')}, ${format(d, 'HH:mm')}`;
                    if (diffDays === 1)
                      return `${t('common.yesterday', 'Gestern')}, ${format(d, 'HH:mm')}`;
                    return fmt(d, 'shortTime');
                  } catch {
                    return iso;
                  }
                };
                const fmtEventTime = () => {
                  try {
                    const d = parseISO(ev.date);
                    const dateStr = fmt(d, 'weekdayShort');
                    if (ev.allDay === false && ev.time) {
                      const end = ev.endTime ? `–${ev.endTime}` : '';
                      return `${dateStr}, ${ev.time}${end}`;
                    }
                    return dateStr;
                  } catch {
                    return ev.date || '';
                  }
                };
                const participantIds =
                  Array.isArray(ev.participantIds) && ev.participantIds.length > 0
                    ? ev.participantIds
                    : ev.createdBy
                    ? [ev.createdBy]
                    : [];
                return (
                  <div
                    key={ev.id}
                    className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl overflow-hidden"
                  >
                    <button
                      type="button"
                      onClick={() => {
                        setViewingEventId(ev.id);
                        setViewingOccurrenceDate(null);
                      }}
                      className="w-full flex items-start gap-3 p-3 sm:p-4 text-left hover:bg-gray-50 dark:hover:bg-gray-800/40 transition-colors"
                    >
                      <div
                        className="w-1 self-stretch rounded-full flex-shrink-0"
                        style={{ backgroundColor: color }}
                      />
                      <div className="min-w-0 flex-1">
                        <div className="font-semibold text-gray-900 dark:text-gray-100 truncate">
                          {ev.title || 'Ereignis'}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 flex items-center gap-1.5 flex-wrap">
                          {sharedCalendar && (
                            <>
                              <UsersIcon size={11} />
                              <span className="truncate">{sharedCalendar.name}</span>
                              <span className="opacity-50">·</span>
                            </>
                          )}
                          <span>{fmtEventTime()}</span>
                        </div>
                      </div>
                      {participantIds.length > 0 && (
                        <div className="flex items-center -space-x-2 flex-shrink-0">
                          {participantIds.slice(0, 3).map((uid) => (
                            <div
                              key={uid}
                              className="ring-2 ring-white dark:ring-gray-900 rounded-full"
                            >
                              {renderCreatorAvatar(uid, 24)}
                            </div>
                          ))}
                        </div>
                      )}
                    </button>
                    <div className="border-t border-gray-100 dark:border-gray-800 divide-y divide-gray-100 dark:divide-gray-800">
                      {entries.slice(0, 5).map((item) => (
                        <div
                          key={item.id}
                          className="flex items-center gap-2 px-3 sm:px-4 py-2 text-xs"
                        >
                          {renderCreatorAvatar(item.userId, 20)}
                          {item.kind === 'audit' ? (
                            <span className="text-gray-700 dark:text-gray-300 truncate flex-1">
                              {auditSummaryDe(item)}
                            </span>
                          ) : (
                            <span className="text-gray-700 dark:text-gray-300 truncate flex-1 flex items-center gap-1.5">
                              <MessageSquare size={11} className="opacity-60 flex-shrink-0" />
                              <span className="truncate">{item.text}</span>
                            </span>
                          )}
                          <span className="text-gray-400 dark:text-gray-500 flex-shrink-0 whitespace-nowrap">
                            {fmtRel(item.at)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Bottom segmented toggle — Calendar | Activity. Fixed to the
          bottom of the viewport (above the iOS home indicator) so it's
          always reachable without stretching. Only relevant in the
          standalone (non-shared, non-birthday) calendar context. */}
      <div className="fixed above-tabbar left-1/2 -translate-x-1/2 z-30 px-2">
        <div className="bg-gray-900/95 dark:bg-gray-800/95 backdrop-blur rounded-full shadow-lg flex p-1 gap-1">
          <button
            type="button"
            onClick={() => setViewMode('calendar')}
            className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors ${
              viewMode === 'calendar'
                ? 'bg-white text-gray-900'
                : 'text-gray-300 hover:text-white'
            }`}
            aria-label="Kalenderansicht"
          >
            <CalendarDays size={16} />
            <span>Kalender</span>
          </button>
          <button
            type="button"
            onClick={() => setViewMode('activity')}
            className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors ${
              viewMode === 'activity'
                ? 'bg-white text-gray-900'
                : 'text-gray-300 hover:text-white'
            }`}
            aria-label="Aktivität"
          >
            <Bell size={16} />
            <span>Aktivität</span>
          </button>
        </div>
      </div>

      {/* Floating Add-event button — mobile only. On desktop the inline
          "Event" button in the header is sufficient. */}
      {viewMode === 'calendar' && (
      <button
        onClick={() => openAddEvent(selectedDate || currentDate)}
        className="sm:hidden fixed right-5 z-30 h-14 w-14 rounded-full shadow-lg bg-primary-600 text-white flex items-center justify-center hover:bg-primary-700 active:scale-95 transition-transform"
        // Stacked above the Kalender/Aktivität switcher, which itself sits
        // above the tab bar.
        style={{ bottom: 'calc(env(safe-area-inset-bottom, 0px) + 9.25rem)' }}
        aria-label="Add event"
      >
        <Plus size={26} />
      </button>
      )}

      {/* Add/Edit Event Modal */}
      {showAddEvent && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={closeModal}
        >
          <div
            className="bg-white rounded-xl p-5 sm:p-6 w-full max-w-md max-h-[90vh] overflow-y-auto overflow-x-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">
                {editingEvent ? 'Event bearbeiten' : 'Neues Event'}
              </h3>
              <button onClick={closeModal} className="p-1 hover:bg-gray-100 rounded">
                <X size={20} />
              </button>
            </div>

            {isSharedView && editingEvent && editingEvent.createdBy && (
              <div className="mb-4 flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                {renderCreatorAvatar(editingEvent.createdBy, 22)}
                <span>
                  {t('sharedCalendar.createdBy')}:{' '}
                  <span className="font-medium text-gray-800 dark:text-gray-100">
                    {membersById[editingEvent.createdBy]?.displayName ||
                      t('sharedCalendar.unknownUser')}
                  </span>
                </span>
              </div>
            )}

            {editingEvent && editingEvent.recurrence && editingEvent.recurrence.freq && (
              <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-start gap-2 text-xs">
                <Repeat size={14} className="text-blue-600 mt-0.5 flex-shrink-0" />
                <div className="text-blue-900">
                  <p className="font-medium">
                    {t('calendar.editingSeries', 'Serie wird bearbeitet')}
                  </p>
                  <p className="text-blue-700">
                    {t('calendar.clickedOn', 'Geklickt auf')}:{' '}
                    {fmt(editingEvent.clickedOccurrenceDate || editingEvent.date, 'long')}
                    {editingEvent.clickedOccurrenceDate !== editingEvent.date && (
                      <>
                        {' '}
                        · {t('calendar.seriesStart', 'Serien‑Start')}:{' '}
                        {fmt(editingEvent.date, 'short')}
                      </>
                    )}
                  </p>
                </div>
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Titel</label>
                <input
                  type="text"
                  autoFocus={!editingEvent}
                  placeholder="Event Titel"
                  className="input"
                  value={newEvent.title}
                  onChange={(e) => {
                    setNewEvent({ ...newEvent, title: e.target.value });
                    setSuggestionsOpen(true);
                  }}
                  onFocus={() => setSuggestionsOpen(true)}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') setSuggestionsOpen(false);
                  }}
                />
                {/* Inline (not floating) so it can never be clipped by the
                    scrolling sheet, and so the form simply grows to fit. */}
                {suggestionsOpen && titleSuggestions.length > 0 && (
                  <ul
                    className="mt-1 rounded-lg border border-gray-200 bg-white divide-y divide-gray-100 overflow-hidden"
                    role="listbox"
                  >
                    {titleSuggestions.map((ev) => {
                      const cat = getCategory(ev.categoryId);
                      return (
                        <li key={ev.id} role="option" aria-selected={false}>
                          <button
                            type="button"
                            // preventDefault keeps the input focused through the
                            // tap, so the list is still there when the click lands.
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => applySuggestion(ev)}
                            className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-gray-50 active:bg-gray-100"
                          >
                            <span
                              className="h-2.5 w-2.5 rounded-full flex-shrink-0"
                              style={{ backgroundColor: cat.color }}
                            />
                            <span className="min-w-0 flex-1">
                              <span className="block truncate text-sm font-medium text-gray-900">
                                {ev.title}
                              </span>
                              <span className="block truncate text-xs text-gray-500">
                                {suggestionMeta(ev)}
                              </span>
                            </span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Label</label>
                <LabelDropdown
                  categories={selectableCategories}
                  value={newEvent.categoryId}
                  onChange={(id) => setNewEvent({ ...newEvent, categoryId: id })}
                />
              </div>

              {isSharedView && (
                <ParticipantPicker
                  members={Object.values(membersById)}
                  value={newEvent.participantIds || []}
                  onChange={(ids) =>
                    setNewEvent({ ...newEvent, participantIds: ids })
                  }
                  renderAvatar={renderCreatorAvatar}
                  t={t}
                />
              )}

              {/* iOS-style time block: all-day toggle + Beginn/Ende rows
                  with date + (optional) time pills aligned to the right. */}
              <div className="rounded-xl bg-gray-50 dark:bg-gray-800/40 border border-gray-200 dark:border-gray-800 divide-y divide-gray-200 dark:divide-gray-800">
                <div className="flex items-center justify-between px-3 py-2.5">
                  <label htmlFor="allDay" className="text-sm font-medium text-gray-800 dark:text-gray-100">
                    Ganztägig
                  </label>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={newEvent.allDay}
                    onClick={() =>
                      setNewEvent({ ...newEvent, allDay: !newEvent.allDay })
                    }
                    className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${
                      newEvent.allDay ? 'bg-primary-600' : 'bg-gray-300 dark:bg-gray-600'
                    }`}
                  >
                    <span
                      className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                        newEvent.allDay ? 'translate-x-5' : 'translate-x-0.5'
                      }`}
                    />
                  </button>
                </div>

                <div className="flex items-center gap-2 px-3 py-2.5">
                  <span className="text-sm font-medium text-gray-800 dark:text-gray-100">Beginn</span>
                  <div className="ml-auto flex items-center gap-2 min-w-0">
                    <input
                      type="date"
                      className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg px-2 py-1.5 text-sm text-gray-900 dark:text-gray-100 min-w-0 focus:outline-none focus:ring-2 focus:ring-primary-500"
                      value={newEvent.date}
                      onChange={(e) => setNewEvent({ ...newEvent, date: e.target.value })}
                    />
                    {!newEvent.allDay && (
                      <input
                        type="time"
                        className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg px-2 py-1.5 text-sm text-gray-900 dark:text-gray-100 min-w-0 focus:outline-none focus:ring-2 focus:ring-primary-500"
                        value={newEvent.startTime}
                        onChange={(e) =>
                          setNewEvent({ ...newEvent, startTime: e.target.value })
                        }
                        onBlur={(e) => {
                          const startTime = e.target.value;
                          if (startTime && !newEvent.endTime) {
                            const [h, m] = startTime.split(':').map(Number);
                            const endH = (h + 1) % 24;
                            setNewEvent({
                              ...newEvent,
                              startTime,
                              endTime: `${String(endH).padStart(2, '0')}:${String(m).padStart(2, '0')}`
                            });
                          }
                        }}
                      />
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2 px-3 py-2.5">
                  <span className="text-sm font-medium text-gray-800 dark:text-gray-100">Ende</span>
                  <div className="ml-auto flex items-center gap-2 min-w-0">
                    <input
                      type="date"
                      className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg px-2 py-1.5 text-sm text-gray-900 dark:text-gray-100 min-w-0 focus:outline-none focus:ring-2 focus:ring-primary-500"
                      value={newEvent.endDate || newEvent.date}
                      min={newEvent.date}
                      onChange={(e) =>
                        setNewEvent({ ...newEvent, endDate: e.target.value })
                      }
                    />
                    {!newEvent.allDay && (
                      <input
                        type="time"
                        className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg px-2 py-1.5 text-sm text-gray-900 dark:text-gray-100 min-w-0 focus:outline-none focus:ring-2 focus:ring-primary-500"
                        value={newEvent.endTime}
                        onChange={(e) =>
                          setNewEvent({ ...newEvent, endTime: e.target.value })
                        }
                      />
                    )}
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notizen</label>
                <textarea
                  placeholder="Zusätzliche Infos..."
                  className="input"
                  rows={3}
                  value={newEvent.notes}
                  onChange={(e) => setNewEvent({ ...newEvent, notes: e.target.value })}
                />
              </div>

              <RecurrenceEditor
                date={newEvent.date}
                recurrence={newEvent.recurrence}
                setRecurrence={(rec) => setNewEvent({ ...newEvent, recurrence: rec })}
              />

              <SportLinker
                sports={data.fitness?.sports || []}
                linkedSportName={newEvent.linkedSportName || ''}
                setLinkedSportName={(name) =>
                  setNewEvent({ ...newEvent, linkedSportName: name || null })
                }
              />

              <div className="flex gap-2 pt-2">
                <button
                  onClick={handleSaveEvent}
                  className="btn bg-primary-600 text-white hover:bg-primary-700 flex-1"
                >
                  {editingEvent ? 'Speichern' : 'Hinzufügen'}
                </button>
                {editingEvent && (
                  <button
                    onClick={() => handleDeleteEvent(editingEvent.id)}
                    className="btn bg-red-100 text-red-600 hover:bg-red-200 flex items-center gap-1"
                  >
                    <Trash2 size={16} />
                  </button>
                )}
                <button onClick={closeModal} className="btn-secondary">
                  Abbrechen
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Event Detail View — opens when an event in the day list is tapped.
          Shows the event in its label color with participant avatars,
          date/time, an activity log, and chat-style comments. */}
      {viewingEventId && (() => {
        // Look up across every event the user can see (not just the current
        // calendar) so the activity feed can open events from any shared
        // calendar without auto-closing.
        const ev = allCalendarEvents.find((e) => e.id === viewingEventId);
        if (!ev) {
          // Event was deleted while open — auto-close.
          setTimeout(() => setViewingEventId(null), 0);
          return null;
        }
        const cat = getCategory(ev.categoryId);
        const color = cat?.color || '#64748b';
        const ids =
          Array.isArray(ev.participantIds) && ev.participantIds.length > 0
            ? ev.participantIds
            : ev.createdBy
            ? [ev.createdBy]
            : [];
        const fmtDate = (iso) =>
          fmt(parseISO(iso), 'weekdayFull');
        const fmtTime = (hhmm) => hhmm || '';
        const sameDay = (ev.endDate || ev.date) === ev.date;

        // Merge audit + comments into one chronological timeline.
        const audit = (ev.audit || []).map((a) => ({ ...a, kind: 'audit' }));
        const comments = (ev.comments || []).map((c) => ({
          ...c,
          kind: 'comment'
        }));
        const timeline = [...audit, ...comments].sort((a, b) =>
          (a.at || '').localeCompare(b.at || '')
        );

        const fieldLabel = (f) => {
          switch (f) {
            case 'title':
              return 'Titel';
            case 'date':
              return 'Datum';
            case 'label':
              return 'Label';
            case 'notes':
              return 'Notizen';
            case 'participants':
              return 'Teilnehmer';
            case 'recurrence':
              return 'Wiederholung';
            default:
              return f;
          }
        };
        const auditSummary = (a) => {
          if (a.type === 'created') return 'Ereignis angelegt';
          const fields = a.fields || [];
          if (fields.length === 0) return 'Ereignis aktualisiert';
          if (fields.length === 1) return `${fieldLabel(fields[0])} aktualisiert`;
          return `${fields.map(fieldLabel).join(', ')} aktualisiert`;
        };
        const fmtRelative = (iso) => {
          try {
            const d = parseISO(iso);
            const today = new Date();
            const diffDays = Math.floor(
              (today.setHours(0, 0, 0, 0) - new Date(d).setHours(0, 0, 0, 0)) /
                86400000
            );
            if (diffDays === 0) return format(d, 'HH:mm');
            if (diffDays === 1) return t('common.yesterday', 'Gestern');
            return fmt(d, 'medium');
          } catch {
            return iso;
          }
        };

        return (
          <div
            className="fixed inset-0 z-[55] bg-black/40 flex items-center justify-center sm:p-4"
            style={{ height: '100dvh' }}
            onClick={() => setViewingEventId(null)}
          >
            <div
              className="bg-white dark:bg-gray-900 w-full sm:max-w-lg sm:rounded-2xl shadow-2xl flex flex-col sm:h-auto sm:max-h-[90vh] overflow-hidden"
              style={{ height: '100dvh' }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header (label-tinted) */}
              <div
                className="px-4 pt-4 pb-5"
                style={{ backgroundColor: `${color}1a` }}
              >
                <div className="flex items-center justify-between">
                  <button
                    onClick={() => setViewingEventId(null)}
                    className="p-1.5 -ml-1 rounded-full hover:bg-black/5 dark:hover:bg-white/5"
                    aria-label="Back"
                    style={{ color }}
                  >
                    <ArrowLeft size={22} />
                  </button>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => {
                        setViewingEventId(null);
                        openEditEvent(ev, viewingOccurrenceDate);
                      }}
                      className="p-1.5 rounded-full hover:bg-black/5 dark:hover:bg-white/5"
                      aria-label="Edit"
                      style={{ color }}
                    >
                      <Pencil size={20} />
                    </button>
                    <button
                      onClick={async () => {
                        // Recurring events get the 3-option dialog (this
                        // occurrence / this and future / whole series) —
                        // a plain confirm here silently deleted the whole
                        // series including past occurrences.
                        if (ev.recurrence && ev.recurrence.freq) {
                          setRecurringDeleteTarget({
                            baseId: ev.id,
                            occurrenceDate: viewingOccurrenceDate || ev.date
                          });
                          setViewingEventId(null);
                          return;
                        }
                        if (
                          await confirm({
                            message: 'Event löschen?',
                            danger: true
                          })
                        ) {
                          commitAllEvents(
                            allCalendarEvents.filter((e) => e.id !== ev.id)
                          );
                          setViewingEventId(null);
                        }
                      }}
                      className="p-1.5 rounded-full hover:bg-black/5 dark:hover:bg-white/5"
                      aria-label="Delete"
                      style={{ color }}
                    >
                      <Trash2 size={20} />
                    </button>
                  </div>
                </div>

                {/* Avatars */}
                {ids.length > 0 && (
                  <div className="mt-2 flex items-center justify-center -space-x-2">
                    {ids.slice(0, 6).map((uid) => (
                      <div
                        key={uid}
                        className="ring-2 rounded-full"
                        style={{ ['--tw-ring-color']: `${color}33` }}
                      >
                        {renderCreatorAvatar(uid, 36)}
                      </div>
                    ))}
                  </div>
                )}

                {/* Title */}
                <h2
                  className="mt-3 text-center text-2xl font-bold leading-tight"
                  style={{ color }}
                >
                  {ev.title}
                </h2>

                {/* Date / time */}
                <div className="mt-3 flex items-center justify-center gap-3 text-sm">
                  <div className="text-center">
                    <div className="font-semibold text-gray-800 dark:text-gray-100">
                      {fmtDate(ev.date)}
                    </div>
                    {!ev.allDay && ev.startTime && (
                      <div
                        className="text-2xl font-bold leading-none mt-0.5"
                        style={{ color }}
                      >
                        {fmtTime(ev.startTime)}
                      </div>
                    )}
                  </div>
                  <ChevronRight size={20} style={{ color }} />
                  <div className="text-center">
                    <div className="font-semibold text-gray-800 dark:text-gray-100">
                      {fmtDate(ev.endDate || ev.date)}
                    </div>
                    {!ev.allDay && ev.endTime && (
                      <div
                        className="text-2xl font-bold leading-none mt-0.5"
                        style={{ color }}
                      >
                        {fmtTime(ev.endTime)}
                      </div>
                    )}
                    {ev.allDay && sameDay && (
                      <div className="text-xs text-gray-500 mt-0.5">Ganztägig</div>
                    )}
                  </div>
                </div>

                {ev.notes && (
                  <p className="mt-3 text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap text-center">
                    {ev.notes}
                  </p>
                )}
              </div>

              {/* Activity timeline */}
              <div className="flex-1 overflow-y-auto px-4 py-4 bg-white dark:bg-gray-900">
                {timeline.length === 0 ? (
                  <div className="text-center text-xs text-gray-400 dark:text-gray-500 py-6">
                    Noch keine Aktivität.
                  </div>
                ) : (
                  <div className="space-y-3">
                    {timeline.map((item, idx) => {
                      const prev = timeline[idx - 1];
                      const showDateHeader =
                        !prev ||
                        format(parseISO(item.at), 'yyyy-MM-dd') !==
                          format(parseISO(prev.at), 'yyyy-MM-dd');
                      const m = membersById[item.userId];
                      const name = m?.displayName || 'Unbekannt';
                      const isMe = user && item.userId === user.id;
                      return (
                        <div key={item.id}>
                          {showDateHeader && (
                            <div className="flex items-center gap-2 my-3 text-[11px] uppercase tracking-wide text-gray-400">
                              <div className="flex-1 border-t border-gray-200 dark:border-gray-800" />
                              <span>{fmtRelative(item.at)}</span>
                              <div className="flex-1 border-t border-gray-200 dark:border-gray-800" />
                            </div>
                          )}

                          {item.kind === 'audit' ? (
                            <div className="flex items-center justify-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                              {renderCreatorAvatar(item.userId, 18)}
                              <span>{auditSummary(item)}</span>
                            </div>
                          ) : (
                            <div
                              className={`flex items-end gap-2 ${
                                isMe ? 'flex-row-reverse' : ''
                              }`}
                            >
                              {!isMe && renderCreatorAvatar(item.userId, 28)}
                              <div
                                className={`max-w-[75%] rounded-2xl px-3 py-2 text-sm ${
                                  isMe
                                    ? 'bg-primary-600 text-white'
                                    : 'bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100'
                                }`}
                                style={
                                  isMe ? { backgroundColor: color } : undefined
                                }
                              >
                                {!isMe && (
                                  <div className="text-[11px] font-semibold opacity-70 mb-0.5">
                                    {name}
                                  </div>
                                )}
                                <div className="whitespace-pre-wrap break-words">
                                  {item.text}
                                </div>
                                <div
                                  className={`text-[10px] mt-1 ${
                                    isMe ? 'text-white/70' : 'text-gray-500'
                                  }`}
                                >
                                  {format(parseISO(item.at), 'HH:mm')}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Comment input */}
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  handleAddComment();
                }}
                className="border-t border-gray-200 dark:border-gray-800 px-2 pt-2 flex items-center gap-2 bg-white dark:bg-gray-900"
                style={{
                  paddingBottom:
                    'max(env(safe-area-inset-bottom), 0.5rem)'
                }}
              >
                <input
                  type="text"
                  value={commentDraft}
                  onChange={(e) => setCommentDraft(e.target.value)}
                  placeholder="Kommentieren…"
                  className="flex-1 bg-gray-100 dark:bg-gray-800 rounded-full px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
                <button
                  type="submit"
                  disabled={!commentDraft.trim()}
                  className="p-2 rounded-full text-white disabled:opacity-40"
                  style={{ backgroundColor: color }}
                  aria-label="Send"
                >
                  <Send size={18} />
                </button>
              </form>
            </div>
          </div>
        );
      })()}

      {/* Recurring Delete Options Modal */}
      {recurringDeleteTarget && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4"
          onClick={() => setRecurringDeleteTarget(null)}
        >
          <div
            className="bg-white rounded-xl w-full max-w-sm overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 pt-5 pb-3 text-center">
              <p className="text-sm text-gray-700">
                Dieser Termin wiederholt sich. Bitte wählen Sie aus, was Sie löschen möchten.
              </p>
            </div>
            <div className="border-t border-gray-200 divide-y divide-gray-200">
              <button
                onClick={deleteOnlyThisOccurrence}
                className="w-full px-5 py-3 text-red-600 font-medium hover:bg-gray-50"
              >
                Nur diesen Termin löschen
              </button>
              <button
                onClick={deleteThisAndFuture}
                className="w-full px-5 py-3 text-red-600 font-medium hover:bg-gray-50"
              >
                Zukünftige Termine löschen
              </button>
              <button
                onClick={deleteEntireSeries}
                className="w-full px-5 py-3 text-red-600 font-medium hover:bg-gray-50"
              >
                Alle Termine löschen
              </button>
              <button
                onClick={() => setRecurringDeleteTarget(null)}
                className="w-full px-5 py-3 text-gray-600 font-medium hover:bg-gray-50"
              >
                Abbrechen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Label Manager Modal */}
      {showLabelManager && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={() => setShowLabelManager(false)}
        >
          <div
            className="bg-white rounded-xl w-full max-w-md max-h-[90vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold">Label-Verwaltung</h3>
              <button
                onClick={() => setShowLabelManager(false)}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-4 overflow-y-auto space-y-2">
              {sortedCategories.map((cat) => (
                <div
                  key={cat.id}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-gray-50"
                >
                  {/* Color indicator - dashed if unset */}
                  <div
                    className={`w-5 h-5 rounded-full flex-shrink-0 ${
                      cat.isCustom ? '' : 'border-2 border-dashed'
                    }`}
                    style={
                      cat.isCustom
                        ? { backgroundColor: cat.color }
                        : { borderColor: cat.color, backgroundColor: 'transparent' }
                    }
                  />
                  {cat.isBirthdayLabel || cat.isTodoLabel ? (
                    <div className="flex-1 flex items-center gap-2 text-sm font-medium" style={{ color: cat.color }}>
                      {cat.isBirthdayLabel ? <Gift size={14} /> : <CheckSquare size={14} />}
                      <span className="truncate">
                        {cat.isBirthdayLabel ? 'Birthday' : cat.name}
                      </span>
                      <span className="ml-auto text-xs text-gray-400 font-normal flex-shrink-0">
                        {cat.isBirthdayLabel
                          ? 'aus Birthday Calender'
                          : cat.todoOwnerName
                          ? t('calendar.fromTodosOf', 'aus To-dos ({name})', {
                              name: cat.todoOwnerName
                            })
                          : t('calendar.fromTodos', 'aus To-dos')}
                      </span>
                    </div>
                  ) : (
                    <input
                      type="text"
                      value={cat.isCustom ? cat.name : ''}
                      onChange={(e) => updateLabel(cat.id, e.target.value)}
                      placeholder={cat.name}
                      className={`flex-1 bg-transparent border-none outline-none text-sm font-medium placeholder:text-gray-400 ${
                        cat.isCustom ? '' : 'text-gray-400'
                      }`}
                      style={cat.isCustom ? { color: cat.color } : {}}
                    />
                  )}
                </div>
              ))}
              <p className="text-xs text-gray-500 px-3 pt-2">
                Tipp: Leere den Namen, um das Label zurückzusetzen.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ----------------- Recurrence Editor -----------------

const PRESET_LABELS = {
  none: 'Nie',
  daily: 'Täglich',
  weekly: 'Wöchentlich',
  monthly: 'Monatlich',
  yearly: 'Jährlich',
  custom: 'Eigene'
};

const FREQ_LABELS = {
  daily: 'Täglich',
  weekly: 'Wöchentlich',
  monthly: 'Monatlich',
  yearly: 'Jährlich'
};

const FREQ_UNIT_SINGULAR = {
  daily: 'Tag',
  weekly: 'Woche',
  monthly: 'Monat',
  yearly: 'Jahr'
};
const FREQ_UNIT_PLURAL = {
  daily: 'Tage',
  weekly: 'Wochen',
  monthly: 'Monate',
  yearly: 'Jahre'
};

const ISO_WEEKDAY_LABELS = [
  { iso: 1, label: 'Montag' },
  { iso: 2, label: 'Dienstag' },
  { iso: 3, label: 'Mittwoch' },
  { iso: 4, label: 'Donnerstag' },
  { iso: 5, label: 'Freitag' },
  { iso: 6, label: 'Samstag' },
  { iso: 7, label: 'Sonntag' }
];

const RecurrenceEditor = ({ date, recurrence, setRecurrence }) => {
  const preset = detectPreset(recurrence, date);
  const isCustom = preset === 'custom';

  const handlePresetChange = (next) => {
    if (next === 'none') {
      setRecurrence(null);
    } else {
      // Preserve per-occurrence deletions — replacing the recurrence object
      // wholesale would drop `exceptions` and resurrect deleted occurrences.
      const exceptions = recurrence?.exceptions;
      const rec = makePresetRecurrence(next, date);
      setRecurrence(exceptions && exceptions.length ? { ...rec, exceptions } : rec);
    }
  };

  const updateRec = (patch) => setRecurrence({ ...(recurrence || {}), ...patch });

  const toggleWeekday = (iso) => {
    const wds = new Set(recurrence?.weekdays || []);
    if (wds.has(iso)) wds.delete(iso);
    else wds.add(iso);
    updateRec({ weekdays: Array.from(wds).sort((a, b) => a - b) });
  };

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
        <Repeat size={14} className="text-gray-500" />
        Wiederholung
      </label>
      <select
        className="input"
        value={preset}
        onChange={(e) => handlePresetChange(e.target.value)}
      >
        {Object.entries(PRESET_LABELS).map(([key, label]) => (
          <option key={key} value={key}>
            {label}
          </option>
        ))}
      </select>

      {isCustom && recurrence && (
        <div className="mt-3 p-3 bg-gray-50 border border-gray-200 rounded-lg space-y-3">
          {/* Häufigkeit */}
          <div className="flex items-center justify-between gap-3">
            <label className="text-sm text-gray-700">Häufigkeit</label>
            <select
              className="bg-white border border-gray-300 rounded px-2 py-1 text-sm"
              value={recurrence.freq}
              onChange={(e) => {
                const next = { ...recurrence, freq: e.target.value };
                if (e.target.value !== 'weekly') delete next.weekdays;
                if (e.target.value === 'weekly' && !recurrence.weekdays) {
                  next.weekdays = [isoWeekday(parseISO(date))];
                }
                setRecurrence(next);
              }}
            >
              {Object.entries(FREQ_LABELS).map(([key, label]) => (
                <option key={key} value={key}>
                  {label}
                </option>
              ))}
            </select>
          </div>

          {/* Interval */}
          <div className="flex items-center justify-between gap-3">
            <label className="text-sm text-gray-700">Jeden</label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                min={1}
                max={99}
                value={recurrence.interval || 1}
                onChange={(e) =>
                  updateRec({ interval: Math.max(1, Number(e.target.value) || 1) })
                }
                className="w-16 bg-white border border-gray-300 rounded px-2 py-1 text-sm text-right"
              />
              <span className="text-sm text-gray-600 w-20">
                {(recurrence.interval || 1) === 1
                  ? FREQ_UNIT_SINGULAR[recurrence.freq]
                  : FREQ_UNIT_PLURAL[recurrence.freq]}
              </span>
            </div>
          </div>

          {/* Weekdays (only weekly) */}
          {recurrence.freq === 'weekly' && (
            <div>
              <p className="text-xs font-medium text-gray-600 mb-2">Festgelegter Tag</p>
              <div className="space-y-1">
                {ISO_WEEKDAY_LABELS.map((wd) => {
                  const active = (recurrence.weekdays || []).includes(wd.iso);
                  return (
                    <button
                      key={wd.iso}
                      type="button"
                      onClick={() => toggleWeekday(wd.iso)}
                      className="w-full flex items-center justify-between px-2 py-1.5 bg-white rounded border border-gray-200 text-sm hover:bg-gray-50"
                    >
                      <span className="text-gray-700">{wd.label}</span>
                      <span
                        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                          active ? 'bg-primary-600' : 'bg-gray-300'
                        }`}
                      >
                        <span
                          className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                            active ? 'translate-x-5' : 'translate-x-1'
                          }`}
                        />
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* End */}
          <div>
            <p className="text-xs font-medium text-gray-600 mb-2">Ende der Wiederholung</p>
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="radio"
                  name="endType"
                  checked={(recurrence.endType || 'never') === 'never'}
                  onChange={() => updateRec({ endType: 'never' })}
                />
                <span>Nie</span>
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="radio"
                  name="endType"
                  checked={recurrence.endType === 'count'}
                  onChange={() => updateRec({ endType: 'count', count: recurrence.count || 10 })}
                />
                <span>Anzahl</span>
                <input
                  type="number"
                  min={1}
                  max={999}
                  disabled={recurrence.endType !== 'count'}
                  value={recurrence.count || 10}
                  onChange={(e) =>
                    updateRec({ count: Math.max(1, Number(e.target.value) || 1) })
                  }
                  className="ml-auto w-20 bg-white border border-gray-300 rounded px-2 py-1 text-sm text-right disabled:opacity-50"
                />
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="radio"
                  name="endType"
                  checked={recurrence.endType === 'until'}
                  onChange={() =>
                    updateRec({
                      endType: 'until',
                      until: recurrence.until || format(addDays(parseISO(date), 30), 'yyyy-MM-dd')
                    })
                  }
                />
                <span>Bis</span>
                <input
                  type="date"
                  disabled={recurrence.endType !== 'until'}
                  value={recurrence.until || ''}
                  min={date}
                  onChange={(e) => updateRec({ until: e.target.value })}
                  className="ml-auto bg-white border border-gray-300 rounded px-2 py-1 text-sm disabled:opacity-50"
                />
              </label>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ----------------- Participant Picker -----------------
// Used inside shared-calendar event forms. Shows a row of currently-tagged
// member avatars that can be removed (×) and an "Add" panel that unfolds
// downward with every member not yet tagged.

const ParticipantPicker = ({ members, value, onChange, renderAvatar, t }) => {
  const [open, setOpen] = useState(false);
  const selectedSet = new Set(value || []);
  const selected = (value || [])
    .map((id) => members.find((m) => m.userId === id))
    .filter(Boolean);
  const remaining = members.filter((m) => !selectedSet.has(m.userId));

  const add = (id) => onChange([...(value || []), id]);
  const remove = (id) => onChange((value || []).filter((x) => x !== id));

  return (
    <div>
      <label className="text-sm font-medium text-gray-700 flex items-center gap-1 mb-1">
        <UsersIcon size={14} className="text-gray-500" />
        {t('sharedCalendar.participants') || 'Teilnehmer'}
      </label>

      {/* Header row: selected chips + chevron toggle */}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="input w-full text-left flex items-center gap-2 min-h-[42px] flex-wrap"
      >
        {selected.length === 0 ? (
          <span className="text-gray-400 text-sm">
            {t('sharedCalendar.addParticipant') || 'Hinzufügen…'}
          </span>
        ) : (
          selected.map((m) => (
            <span
              key={m.userId}
              className="inline-flex items-center gap-1.5 pl-1 pr-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-800 text-xs"
              onClick={(e) => e.stopPropagation()}
            >
              {renderAvatar(m.userId, 20)}
              <span className="font-medium text-gray-800 dark:text-gray-100">
                {m.displayName || '—'}
              </span>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  remove(m.userId);
                }}
                className="text-gray-400 hover:text-red-500"
                aria-label="Remove"
              >
                <X size={12} />
              </button>
            </span>
          ))
        )}
        <ChevronRight
          size={16}
          className={`ml-auto text-gray-400 transition-transform ${
            open ? 'rotate-90' : ''
          }`}
        />
      </button>

      {/* Unfolded panel: members not yet selected */}
      {open && (
        <div className="mt-2 border border-gray-200 dark:border-gray-800 rounded-lg overflow-hidden">
          {remaining.length === 0 ? (
            <div className="px-3 py-3 text-sm text-gray-500">
              {t('sharedCalendar.allAdded') || 'Alle Mitglieder sind dabei.'}
            </div>
          ) : (
            <ul className="divide-y divide-gray-100 dark:divide-gray-800">
              {remaining.map((m) => (
                <li
                  key={m.userId}
                  className="flex items-center gap-3 px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-800/40"
                >
                  {renderAvatar(m.userId, 28)}
                  <span className="flex-1 text-sm font-medium text-gray-800 dark:text-gray-100 truncate">
                    {m.displayName || '—'}
                  </span>
                  <button
                    type="button"
                    onClick={() => add(m.userId)}
                    className="p-1.5 rounded-full bg-primary-600 text-white hover:bg-primary-700"
                    aria-label="Add"
                  >
                    <Plus size={14} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

// ----------------- Sport Linker -----------------

const SportLinker = ({ sports, linkedSportName, setLinkedSportName }) => {
  const enabled = !!linkedSportName;

  return (
    <div>
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium text-gray-700 flex items-center gap-1">
          <Trophy size={14} className="text-gray-500" />
          Als Sport eintragen
        </label>
        <button
          type="button"
          onClick={() =>
            setLinkedSportName(enabled ? '' : sports[0]?.name || '')
          }
          disabled={sports.length === 0 && !enabled}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
            enabled ? 'bg-primary-600' : 'bg-gray-300'
          } disabled:opacity-50`}
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
              enabled ? 'translate-x-6' : 'translate-x-1'
            }`}
          />
        </button>
      </div>

      {enabled && sports.length > 0 && (
        <select
          className="input mt-2"
          value={linkedSportName}
          onChange={(e) => setLinkedSportName(e.target.value)}
        >
          {sports.map((sport) => (
            <option key={sport.id} value={sport.name}>
              {sport.name}
            </option>
          ))}
        </select>
      )}

      {sports.length === 0 && (
        <p className="text-xs text-gray-400 mt-1">
          Lege zuerst eine Sportart unter Fitness → Sports an.
        </p>
      )}

      {enabled && (
        <p className="text-xs text-gray-500 mt-2">
          Vergangene Wiederholungen werden automatisch als Workouts angelegt.
        </p>
      )}
    </div>
  );
};

export default Calendar;
